import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Sparkles, 
  Save, 
  Trash2, 
  Plus, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldAlert, 
  Settings, 
  Layers, 
  Cpu, 
  ArrowLeft, 
  Edit3, 
  Eye, 
  Check, 
  RotateCcw, 
  Activity, 
  User, 
  Heart, 
  Zap, 
  Copy, 
  Sliders, 
  HelpCircle,
  ExternalLink,
  ShieldCheck,
  MapPin,
  Crosshair,
  Download,
  Upload,
  CloudUpload,
  Cloud,
  Loader2,
  Globe,
  FileImage,
  ArrowUp,
  ArrowDown,
  ArrowRightLeft,
  ChevronUp,
  ChevronDown,
  Columns,
  Maximize2,
  ListOrdered
} from 'lucide-react';
import { 
  ClinicalSimulatorScenario, 
  CertificationType, 
  SkillSlide, 
  SimulatorHotspot, 
  SimulatorHotspotAction, 
  SimulatorInstantTrap 
} from '../../types';
import { StorageService, normalizeClinicalSimulatorScenario } from '../../services/storageService';
import { SponsoredService } from '../../services/sponsoredService';
import { generateClinicalSimulatorScenario, generateStationActionsWithAi } from '../../services/geminiService';
import { 
  CLINICAL_ENVIRONMENTS, 
  CLINICAL_SKILL_FIXTURES, 
  buildSmartClinicalPrompt, 
  resolveSkillEnvironmentPreset,
  HANDWASHING_HOTSPOTS_PRESET,
  coords2DtoSpherical,
  sphericalTo2DCoords
} from '../../data/clinicalEnvironments';
import { ClinicalRoomStage } from '../../components/simulator/ClinicalRoomStage';
import { ClinicalPhotosphereStage } from '../../components/simulator/ClinicalPhotosphereStage';
import { useThemeTokens } from '../../hooks/useActiveTheme';

export const ClinicalSimulatorStudio: React.FC = () => {
  const theme = useThemeTokens();

  // Certifications & Skills State
  const [certs, setCerts] = useState<CertificationType[]>([]);
  const [activeCertId, setActiveCertId] = useState<string>('cna');
  const [availableSkills, setAvailableSkills] = useState<SkillSlide[]>([]);
  const [scenarios, setScenarios] = useState<ClinicalSimulatorScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<ClinicalSimulatorScenario | null>(null);

  // Feature Flag State
  const [isGlobalSimulatorEnabled, setIsGlobalSimulatorEnabled] = useState(true);
  const [enabledCerts, setEnabledCerts] = useState<string[]>(['cna', 'phlebotomy', 'medical_assistant', 'pharmacy_tech', 'pct']);
  const [isUpdatingFlag, setIsUpdatingFlag] = useState(false);

  // UI Modes & Tabs
  const [activeTab, setActiveTab] = useState<'editor' | 'sandbox'>('editor');
  const [editorSubTab, setEditorSubTab] = useState<'overview' | 'patient' | 'hotspots' | 'traps' | 'rubric'>('overview');
  const [selectedHotspotIndex, setSelectedHotspotIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Studio Photorealistic Imagery State
  const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiEnvironment, setAiEnvironment] = useState<'bedside_room' | 'exam_room' | 'phlebotomy_chair' | 'icu_bay' | 'handwashing_station' | string>('bedside_room');
  const [selectedAiComponents, setSelectedAiComponents] = useState<string[]>([
    'candidate', 'sink', 'faucet', 'soap', 'towels', 'waste_bin', 'hygiene_poster'
  ]);
  const [isGeneratingAiImage, setIsGeneratingAiImage] = useState(false);
  const [generatedAiImageUrl, setGeneratedAiImageUrl] = useState<string | null>(null);
  const [wixUrlInput, setWixUrlInput] = useState('');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [studioPlacerMode, setStudioPlacerMode] = useState<'2d' | '3d' | 'split'>('2d');
  const [studio2DClickRipple, setStudio2DClickRipple] = useState<{ x: number; y: number } | null>(null);
  const [photosphereUrlInput, setPhotosphereUrlInput] = useState('');
  const [is360PanoramaMode, setIs360PanoramaMode] = useState(false);
  const [includeStudentActor, setIncludeStudentActor] = useState(true);
  const [isUploadingToWix, setIsUploadingToWix] = useState(false);
  const studioFileInputRef = React.useRef<HTMLInputElement>(null);

  // Sync wixUrlInput & photosphereUrlInput when selectedScenario changes
  useEffect(() => {
    if (selectedScenario) {
      setWixUrlInput(selectedScenario.customImageUrl || selectedScenario.imageUrl || '');
      setPhotosphereUrlInput(selectedScenario.photosphereUrl || '');
    }
  }, [selectedScenario?.id]);

  // Load certifications & initial config
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [certList, adConfig] = await Promise.all([
          StorageService.getCertifications(),
          SponsoredService.getConfig()
        ]);
        setCerts(certList);
        if (certList.length > 0) {
          const urlParams = new URLSearchParams(window.location.search);
          const urlCert = urlParams.get('certId');
          const savedCert = localStorage.getItem('cna_admin_active_cert');
          const preferredCert = urlCert || savedCert;
          if (preferredCert && certList.some(c => c.id === preferredCert)) {
            setActiveCertId(preferredCert);
          } else {
            setActiveCertId(certList[0].id);
          }
        }
        if (adConfig) {
          setIsGlobalSimulatorEnabled(adConfig.isClinicalSimulatorEnabled !== false);
          if (adConfig.clinicalSimulatorEnabledCerts) {
            setEnabledCerts(adConfig.clinicalSimulatorEnabledCerts);
          }
        }
      } catch (e) {
        console.error("Initial studio data load failed", e);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Sync with cross-tab / cross-component simulator updates
  useEffect(() => {
    const handleSimUpdated = (e: any) => {
      if (e.detail?.certId === activeCertId && e.detail?.scenario) {
        const incoming = normalizeClinicalSimulatorScenario(e.detail.scenario, activeCertId);
        setScenarios(prev => {
          const idx = prev.findIndex(s => s.id === incoming.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = incoming;
            return next;
          }
          return [incoming, ...prev];
        });
      }
    };
    window.addEventListener('clinical-simulators-updated', handleSimUpdated);
    return () => window.removeEventListener('clinical-simulators-updated', handleSimUpdated);
  }, [activeCertId]);

  // Load skills & scenarios whenever activeCertId changes
  useEffect(() => {
    let isMounted = true;
    const loadCertScenarios = async () => {
      const cleanCertId = (activeCertId || 'cna').toLowerCase().trim();
      try {
        const [skillsData, scenarioList] = await Promise.all([
          StorageService.getSkillsSummary(cleanCertId).catch(() => []),
          StorageService.getClinicalSimulators(cleanCertId).catch(async (err) => {
            console.error("Failed to fetch clinical simulators, falling back to cache:", err);
            try {
              const localKey = `cna_clinical_sims_${cleanCertId}`;
              const localRaw = localStorage.getItem(localKey);
              if (localRaw) {
                const parsed = JSON.parse(localRaw);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
              }
            } catch (e) {}
            return [];
          })
        ]);
        if (!isMounted) return;
        const normalizedScenarios = (scenarioList || [])
          .filter(s => s && s.id)
          .map(s => normalizeClinicalSimulatorScenario(s, cleanCertId));
        setAvailableSkills(skillsData || []);
        setScenarios(normalizedScenarios);

        if (normalizedScenarios.length > 0) {
          const urlParams = new URLSearchParams(window.location.search);
          const urlScenarioId = urlParams.get('scenarioId');
          const lastActiveId = urlScenarioId || localStorage.getItem(`cna_last_active_sim_${cleanCertId}`);
          const match = lastActiveId ? normalizedScenarios.find(s => s.id === lastActiveId) : null;
          setSelectedScenario(match || normalizedScenarios[0]);
        } else {
          setSelectedScenario(null);
        }
      } catch (e) {
        console.error("Failed to load cert scenarios", e);
      }
    };
    loadCertScenarios();
    return () => { isMounted = false; };
  }, [activeCertId]);

  // Flash status message
  const flashStatus = (type: 'success' | 'error' | 'info', text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // Toggle Global Feature Flag
  const handleToggleGlobalFlag = async () => {
    setIsUpdatingFlag(true);
    const nextVal = !isGlobalSimulatorEnabled;
    setIsGlobalSimulatorEnabled(nextVal);
    try {
      await SponsoredService.updateConfig({ isClinicalSimulatorEnabled: nextVal });
      flashStatus('success', `Clinical Simulator platform feature is now ${nextVal ? 'ENABLED' : 'DISABLED'}.`);
    } catch (e) {
      flashStatus('error', 'Failed to update feature flag.');
    } finally {
      setIsUpdatingFlag(false);
    }
  };

  // Toggle Cert Feature Flag
  const handleToggleCertFlag = async (cId: string) => {
    setIsUpdatingFlag(true);
    const nextList = enabledCerts.includes(cId)
      ? enabledCerts.filter(id => id !== cId)
      : [...enabledCerts, cId];
    setEnabledCerts(nextList);
    try {
      await SponsoredService.updateConfig({ clinicalSimulatorEnabledCerts: nextList });
      flashStatus('success', `Clinical Simulator for ${cId.toUpperCase()} updated.`);
    } catch (e) {
      flashStatus('error', 'Failed to update cert flag.');
    } finally {
      setIsUpdatingFlag(false);
    }
  };

  // Create new skill-aligned scenario
  const handleCreateNewScenario = async () => {
    const cleanCertId = (activeCertId || 'cna').toLowerCase().trim();
    const activeCertObj = certs.find(c => (c.id || '').toLowerCase().trim() === cleanCertId);
    
    // Choose the first available unassigned skill for this certification, or first skill
    const firstSkill = availableSkills.find(s => !scenarios.some(sc => sc.skillId === s.id)) || availableSkills[0];
    const initialTitle = firstSkill ? `${firstSkill.name} - Clinical Simulation` : `New ${activeCertObj?.shortName || cleanCertId.toUpperCase()} Clinical Simulation`;
    
    const preferredEnv = cleanCertId === 'phlebotomy' ? 'phlebotomy_chair' : cleanCertId === 'medical_assistant' ? 'exam_room' : 'bedside_room';
    const preset = resolveSkillEnvironmentPreset(firstSkill?.name || initialTitle, preferredEnv);

    const newSim: ClinicalSimulatorScenario = {
      id: `sim_${cleanCertId}_${Date.now()}`,
      skillId: firstSkill?.id || '',
      certId: cleanCertId,
      version: 1,
      status: 'draft',
      title: initialTitle,
      description: `Step-by-step interactive clinical examination simulation for ${firstSkill?.name || 'patient care'}.`,
      environment: preset.environment as any,
      enable3dPhotosphere: true,
      photosphereUrl: preset.photosphereUrl,
      imageUrl: preset.imageUrl,
      patient: {
        name: 'Resident Patient',
        age: 72,
        roomNumber: '101-A',
        primaryDiagnosis: 'Standard clinical evaluation',
        mobilityStatus: 'Requires assistance with mobility',
        precautions: ['Fall Risk', 'Standard Precautions'],
        baselineVitals: { bp: '120/80', hr: 72, rr: 16, spo2: 98, temp: '98.6°F' }
      },
      rubricMilestones: {
        infectionControl: { totalWeight: 25, requiredActions: ['act_wash_hands'] },
        residentDignity: { totalWeight: 25, requiredActions: ['act_greet'] },
        safetyMechanics: { totalWeight: 25, requiredActions: ['act_bed_lock'] },
        proceduralTechnique: { totalWeight: 25, requiredActions: ['act_main_procedure'] }
      },
      instantFailTraps: [
        {
          id: 'trap_critical_fail',
          name: 'Breach of Patient Safety Precaution',
          triggerCondition: 'neglect_safety_standard',
          boardRationale: 'State Board Mandatory Fail: Failure to protect resident safety or breach of infection control.'
        }
      ],
      hotspots: [
        {
          id: 'hotspot_sink',
          name: 'Hand Hygiene Sink Station',
          zone: 'sink',
          coordinates: { x: 18, y: 55 },
          sphericalCoordinates: { yaw: 110, pitch: -8 },
          requiredPhase: 'any',
          availableActions: [
            {
              actionId: 'act_wash_hands',
              label: 'Step 1: Perform Hand Hygiene (20 seconds friction)',
              feedbackDialogue: 'Examiner notes proper hand hygiene technique verified.',
              isCritical: true,
              pointsAwarded: 15,
              category: 'hygiene',
              stateTransitions: { handsClean: true }
            }
          ]
        },
        {
          id: 'hotspot_resident',
          name: 'Resident',
          zone: 'resident',
          coordinates: { x: 50, y: 50 },
          sphericalCoordinates: { yaw: 0, pitch: 0 },
          requiredPhase: 'any',
          availableActions: [
            {
              actionId: 'act_greet',
              label: 'Step 2: Knock, Greet Resident by Name & Check ID',
              feedbackDialogue: 'Resident welcomes you and confirms readiness.',
              isCritical: true,
              pointsAwarded: 10,
              category: 'dignity'
            },
            {
              actionId: 'act_main_procedure',
              label: `Step 3: Execute ${firstSkill?.name || 'Core Skill'} Procedure Steps`,
              feedbackDialogue: 'Candidate follows procedural protocol accurately.',
              isCritical: true,
              pointsAwarded: 25,
              category: 'technique'
            }
          ]
        },
        {
          id: 'hotspot_call_light',
          name: 'Call Light & Bedside Controls',
          zone: 'call_light',
          coordinates: { x: 75, y: 50 },
          sphericalCoordinates: { yaw: -65, pitch: -10 },
          requiredPhase: 'exit',
          availableActions: [
            {
              actionId: 'act_bed_lock',
              label: 'Step 4: Lock Bed Brakes & Place Call Light in Reach',
              feedbackDialogue: 'Resident has call light in hand; brakes verified locked.',
              isCritical: true,
              pointsAwarded: 15,
              category: 'safety',
              stateTransitions: { bedLocked: true, callLightInReach: true }
            }
          ]
        }
      ],
      initialState: {
        curtainDrawn: false,
        bedHeight: 'low',
        bedLocked: false,
        handsClean: false,
        glovesOn: false,
        nonSkidShoesOn: false,
        callLightInReach: false,
        residentComfort: 90
      }
    };

    setSelectedScenario(newSim);
    setScenarios(prev => [newSim, ...prev.filter(s => s.id !== newSim.id)]);
    localStorage.setItem(`cna_last_active_sim_${cleanCertId}`, newSim.id);
    setWixUrlInput(preset.imageUrl);
    setPhotosphereUrlInput(preset.photosphereUrl);
    
    // Immediately persist new scenario so it will never disappear on reload
    await StorageService.saveClinicalSimulator(cleanCertId, newSim);
    flashStatus('info', `New 360° scenario initialized for "${firstSkill?.name || 'patient care'}" with skill-aligned environment.`);
  };

  // AI Generation from current skill
  const handleGenerateWithAi = async () => {
    if (!selectedScenario) return;
    const cleanCertId = (activeCertId || 'cna').toLowerCase().trim();
    
    // Look up active skill or match by title
    let activeSkill = availableSkills.find(s => s.id === selectedScenario.skillId);
    if (!activeSkill && selectedScenario.title) {
      activeSkill = availableSkills.find(s => 
        selectedScenario.title.toLowerCase().includes(s.name.toLowerCase()) || 
        s.name.toLowerCase().includes(selectedScenario.title.toLowerCase())
      );
    }
    if (!activeSkill) {
      activeSkill = {
        id: selectedScenario.skillId || `skill_${Date.now()}`,
        name: selectedScenario.title,
        icon: 'Activity',
        slides: []
      };
    }
    const activeCertObj = certs.find(c => (c.id || '').toLowerCase().trim() === cleanCertId);

    setIsGeneratingAi(true);
    try {
      const generated = await generateClinicalSimulatorScenario(
        activeSkill,
        activeCertObj?.name || 'Nursing Assistant',
        cleanCertId
      );
      generated.certId = cleanCertId;
      if (activeSkill.id && !generated.skillId) {
        generated.skillId = activeSkill.id;
      }
      setSelectedScenario(generated);
      setWixUrlInput(generated.customImageUrl || generated.imageUrl || '');
      setPhotosphereUrlInput(generated.photosphereUrl || '');
      localStorage.setItem(`cna_last_active_sim_${cleanCertId}`, generated.id);
      
      // Update in local list
      setScenarios(prev => {
        const idx = prev.findIndex(s => s.id === generated.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = generated;
          return next;
        }
        return [generated, ...prev];
      });
      await StorageService.saveClinicalSimulator(cleanCertId, generated);
      flashStatus('success', `✓ AI successfully generated all procedural steps & 360° environment for "${activeSkill.name}"!`);
    } catch (e: any) {
      console.error("AI Generation error:", e);
      flashStatus('error', 'AI scenario generation encountered an issue. Fallback scenario provided.');
    } finally {
      setIsGeneratingAi(false);
    }
  };

  // Instantly persist scenario updates to state, database, and skill linkage
  const persistScenario = async (updated: ClinicalSimulatorScenario, successMsg?: string) => {
    const cleanCertId = (activeCertId || updated.certId || 'cna').toLowerCase().trim();
    const scenarioToPersist: ClinicalSimulatorScenario = { ...updated, certId: cleanCertId };
    setSelectedScenario(scenarioToPersist);
    localStorage.setItem(`cna_last_active_sim_${cleanCertId}`, scenarioToPersist.id);
    setScenarios(prev => {
      const idx = prev.findIndex(s => s.id === scenarioToPersist.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = scenarioToPersist;
        return next;
      }
      return [scenarioToPersist, ...prev];
    });
    try {
      await StorageService.saveClinicalSimulator(cleanCertId, scenarioToPersist);
      
      // Update skill attachment if skillId matches
      if (scenarioToPersist.skillId) {
        try {
          const fullSkill = await StorageService.getSkillById(cleanCertId, scenarioToPersist.skillId);
          if (fullSkill) {
            fullSkill.hasSimulator = scenarioToPersist.status !== 'archived';
            fullSkill.simulatorScenarioId = scenarioToPersist.id;
            await StorageService.saveSkill(cleanCertId, fullSkill);
          }
        } catch (err) {}
      }
      if (successMsg) {
        flashStatus('success', successMsg);
      }
    } catch (e: any) {
      flashStatus('error', `Save failed: ${e.message}`);
    }
  };

  // Save Scenario to Server & Local Storage
  const handleSaveScenario = async () => {
    if (!selectedScenario) return;
    try {
      const cleanCertId = (activeCertId || selectedScenario.certId || 'cna').toLowerCase().trim();
      const envInfo = CLINICAL_ENVIRONMENTS[selectedScenario.environment] || CLINICAL_ENVIRONMENTS['bedside_room'];
      const customImg = wixUrlInput.trim();
      const rawPhoto = photosphereUrlInput.trim();

      const finalImageUrl = customImg || envInfo?.imageUrl || selectedScenario.imageUrl || '/images/environments/bedside_room.jpg';
      const finalPhotosphereUrl = rawPhoto || (customImg ? customImg : (envInfo?.photosphereUrl || finalImageUrl));

      const scenarioToSave: ClinicalSimulatorScenario = { 
        ...selectedScenario, 
        certId: cleanCertId,
        customImageUrl: customImg,
        imageUrl: finalImageUrl,
        photosphereUrl: finalPhotosphereUrl,
        enable3dPhotosphere: selectedScenario.enable3dPhotosphere !== false
      };
      localStorage.setItem(`cna_last_active_sim_${cleanCertId}`, scenarioToSave.id);
      
      const saved = await StorageService.saveClinicalSimulator(cleanCertId, scenarioToSave);
      const finalScenario = saved && saved.id ? saved : scenarioToSave;
      const normalizedFinal = normalizeClinicalSimulatorScenario(finalScenario, cleanCertId);
      setSelectedScenario(normalizedFinal);
      setWixUrlInput(normalizedFinal.customImageUrl || '');
      setPhotosphereUrlInput(normalizedFinal.photosphereUrl || '');
      
      // If scenario was previously attached to another skill, unbind old skill
      const previousScenario = scenarios.find(s => s.id === normalizedFinal.id);
      if (previousScenario?.skillId && previousScenario.skillId !== normalizedFinal.skillId) {
        try {
          const oldSkill = await StorageService.getSkillById(cleanCertId, previousScenario.skillId);
          if (oldSkill && oldSkill.simulatorScenarioId === normalizedFinal.id) {
            oldSkill.simulatorScenarioId = undefined;
            oldSkill.hasSimulator = false;
            await StorageService.saveSkill(cleanCertId, oldSkill);
          }
        } catch (err) {}
      }

      // Also update skill attachment ONLY if valid skillId is specified
      if (normalizedFinal.skillId && normalizedFinal.skillId !== 'none' && normalizedFinal.skillId !== 'unassigned') {
        try {
          const fullSkill = await StorageService.getSkillById(cleanCertId, normalizedFinal.skillId);
          if (fullSkill) {
            fullSkill.hasSimulator = normalizedFinal.status !== 'archived';
            fullSkill.simulatorScenarioId = normalizedFinal.id;
            await StorageService.saveSkill(cleanCertId, fullSkill);
          }
        } catch (err) {}
      }

      setScenarios(prev => {
        const idx = prev.findIndex(s => s.id === normalizedFinal.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = normalizedFinal;
          return next;
        }
        return [normalizedFinal, ...prev];
      });

      // Broadcast update event so all open views (like SkillsLab) update immediately
      window.dispatchEvent(new CustomEvent('clinical-simulators-updated', {
        detail: { certId: cleanCertId, scenario: normalizedFinal }
      }));

      flashStatus('success', `Scenario "${normalizedFinal.title}" saved successfully! Live changes applied for all users.`);
    } catch (e: any) {
      flashStatus('error', `Failed to save scenario: ${e.message}`);
    }
  };

  // Delete Scenario
  const handleDeleteScenario = async (id: string) => {
    if (!confirm('Are you sure you want to delete this clinical simulation scenario?')) return;
    const cleanCertId = (activeCertId || 'cna').toLowerCase().trim();
    try {
      const scenarioToDelete = scenarios.find(s => s.id === id);
      if (scenarioToDelete?.skillId) {
        try {
          const fullSkill = await StorageService.getSkillById(cleanCertId, scenarioToDelete.skillId);
          if (fullSkill && fullSkill.simulatorScenarioId === id) {
            fullSkill.simulatorScenarioId = undefined;
            fullSkill.hasSimulator = false;
            await StorageService.saveSkill(cleanCertId, fullSkill);
          }
        } catch (err) {}
      }
      await StorageService.deleteClinicalSimulator(cleanCertId, id);
      const remaining = scenarios.filter(s => s.id !== id);
      setScenarios(remaining);
      const nextActive = remaining[0] || null;
      setSelectedScenario(nextActive);
      if (nextActive) {
        localStorage.setItem(`cna_last_active_sim_${cleanCertId}`, nextActive.id);
      } else {
        localStorage.removeItem(`cna_last_active_sim_${cleanCertId}`);
      }
      flashStatus('info', 'Scenario deleted.');
    } catch (e: any) {
      flashStatus('error', `Delete failed: ${e.message}`);
    }
  };

  // Download active photorealistic image for Wix CDN hosting
  const handleDownloadActiveImage = async () => {
    if (!selectedScenario) return;
    const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
    const activeImg = selectedScenario.customImageUrl || selectedScenario.imageUrl || envDef.imageUrl || '/images/environments/bedside_room.jpg';
    try {
      const response = await fetch(activeImg);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photorealistic_${selectedScenario.environment || 'room'}_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      flashStatus('success', 'Image downloaded! You can now host it on Wix CDN.');
    } catch {
      window.open(activeImg, '_blank');
    }
  };

  // Apply custom Wix CDN or hosted image URL
  const handleApplyWixUrl = async () => {
    if (!selectedScenario) return;
    const trimmed = wixUrlInput.trim();
    if (!trimmed) return;

    // If user provided a base64 string, automatically upload to Wix Media Manager and delete base64!
    if (trimmed.startsWith('data:')) {
      await handleUploadGeneratedImageToWix(trimmed);
      return;
    }

    setSelectedScenario(prev => {
      if (!prev) return prev;
      const isDefaultPhoto = !prev.photosphereUrl || prev.photosphereUrl.startsWith('/images/environments/');
      return {
        ...prev,
        customImageUrl: trimmed,
        imageUrl: trimmed,
        photosphereUrl: isDefaultPhoto ? trimmed : prev.photosphereUrl
      };
    });
    setPhotosphereUrlInput(prev => (!prev || prev.startsWith('/images/environments/')) ? trimmed : prev);
    flashStatus('success', 'Custom image URL applied to both room imagery and 360 stage. Click "Save Scenario" to commit.');
  };

  // Revert back to built-in environment image preset
  const handleClearCustomUrl = () => {
    if (!selectedScenario) return;
    setWixUrlInput('');
    setPhotosphereUrlInput('');
    setSelectedScenario(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        customImageUrl: '',
        imageUrl: '',
        photosphereUrl: ''
      };
    });
    flashStatus('info', 'Reverted to environmental preset imagery');
  };

  // Upload local image directly
  const handleStudioFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedScenario) return;
    try {
      setUploadStatus('Uploading image...');
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result as string;
        const res = await fetch('/practice-exams/api/clinical-sim/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Data, fileName: file.name })
        });
        const data = await res.json();
        if (data.url) {
          setSelectedScenario(prev => {
            if (!prev) return prev;
            const isDefaultPhoto = !prev.photosphereUrl || prev.photosphereUrl.startsWith('/images/environments/');
            return { 
              ...prev, 
              customImageUrl: data.url, 
              imageUrl: data.url,
              photosphereUrl: isDefaultPhoto ? data.url : prev.photosphereUrl
            };
          });
          setWixUrlInput(data.url);
          setPhotosphereUrlInput(prev => (!prev || prev.startsWith('/images/environments/')) ? data.url : prev);
          setUploadStatus('Uploaded successfully!');
          setTimeout(() => setUploadStatus(null), 3000);
          flashStatus('success', 'Image uploaded and linked to scenario & 360 stage. Click "Save Scenario" to commit.');
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadStatus('Upload failed');
    }
  };

  // Direct upload of AI-generated or custom image to Wix Media Manager
  const handleUploadGeneratedImageToWix = async (customImageSource?: string) => {
    const rawTarget = customImageSource || generatedAiImageUrl || selectedScenario?.customImageUrl || selectedScenario?.imageUrl;
    if (!rawTarget) {
      flashStatus('error', 'No image available to upload to Wix Media.');
      return;
    }

    if (rawTarget.includes('static.wixstatic.com/media/')) {
      flashStatus('info', 'Image is already hosted on Wix Media CDN.');
      return;
    }

    setIsUploadingToWix(true);
    try {
      let base64Payload = rawTarget;

      // If it's a relative URL or remote URL, convert it to base64 before uploading
      if (!rawTarget.startsWith('data:')) {
        try {
          const resp = await fetch(rawTarget);
          const blob = await resp.blob();
          base64Payload = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (fetchErr) {
          console.warn('Could not convert URL to base64, passing URL directly', fetchErr);
        }
      }

      const safeTitle = (selectedScenario?.title || 'clinical_stage')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .slice(0, 30);
      const is360 = is360PanoramaMode || selectedScenario?.enable3dPhotosphere;
      const fileName = `${is360 ? 'clinical_360_' : 'clinical_env_'}${safeTitle}_${Date.now()}.jpg`;

      const res = await fetch('/practice-exams/api/clinical-sim/upload-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64Data: base64Payload,
          fileName,
          compress: true
        })
      });

      const data = await res.json();
      const newWixUrl = data.wixCdnUrl || data.url;

      if (!newWixUrl) {
        throw new Error(data.error || 'Failed to obtain Wix CDN URL');
      }

      // 1. Update studio inputs with hosted Wix CDN URL
      setGeneratedAiImageUrl(newWixUrl);
      setWixUrlInput(newWixUrl);
      setPhotosphereUrlInput(prev => (!prev || prev.startsWith('data:') || prev.startsWith('/images/') || prev === rawTarget) ? newWixUrl : prev);

      // 2. AUTOMATICALLY DELETE ANY BASE64 REFERENCES from scenario
      if (selectedScenario) {
        const cleanCertId = (activeCertId || selectedScenario.certId || 'cna').toLowerCase().trim();
        const updatedScenario: ClinicalSimulatorScenario = {
          ...selectedScenario,
          customImageUrl: newWixUrl,
          imageUrl: newWixUrl,
          photosphereUrl: (selectedScenario.photosphereUrl?.startsWith('data:') || !selectedScenario.photosphereUrl || selectedScenario.photosphereUrl === rawTarget) 
            ? newWixUrl 
            : selectedScenario.photosphereUrl
        };
        setSelectedScenario(updatedScenario);

        // Immediately persist the clean scenario so base64 never bloats localStorage or database!
        await StorageService.saveClinicalSimulator(cleanCertId, updatedScenario);
      }

      // Explicitly dereference any local base64 buffers
      base64Payload = '';

      flashStatus('success', '✓ Image hosted on Wix Media CDN! Base64 references automatically deleted.');
    } catch (err: any) {
      console.error('Wix upload failed:', err);
      flashStatus('error', `Failed to upload to Wix: ${err.message || 'Check network / credentials'}`);
    } finally {
      setIsUploadingToWix(false);
    }
  };

  // Open AI Photorealistic Image Synthesizer with intelligent defaults
  const handleOpenAiImageModal = () => {
    if (!selectedScenario) return;
    const preset = resolveSkillEnvironmentPreset(selectedScenario.title, selectedScenario.environment);
    const targetEnv = preset.environment;
    setAiEnvironment(targetEnv);
    const is360 = Boolean(selectedScenario.enable3dPhotosphere);
    setIs360PanoramaMode(is360);

    const initialFixtures = preset.defaultComponentIds.length > 0
      ? preset.defaultComponentIds
      : CLINICAL_SKILL_FIXTURES
          .filter(f => f.defaultForEnvs.includes(targetEnv))
          .map(f => f.id);
    setSelectedAiComponents(initialFixtures);

    const autoPrompt = buildSmartClinicalPrompt(
      targetEnv,
      selectedScenario.title,
      initialFixtures,
      is360,
      includeStudentActor
    );
    setAiPrompt(autoPrompt);
    setIsAiImageModalOpen(true);
  };

  // Switch Environment in AI Modal and auto-regenerate prompt
  const handleAiEnvironmentChange = (newEnv: string) => {
    setAiEnvironment(newEnv);
    const newFixtures = CLINICAL_SKILL_FIXTURES
      .filter(f => f.defaultForEnvs.includes(newEnv))
      .map(f => f.id);
    setSelectedAiComponents(newFixtures);
    const updatedPrompt = buildSmartClinicalPrompt(
      newEnv,
      selectedScenario?.title,
      newFixtures,
      is360PanoramaMode,
      includeStudentActor
    );
    setAiPrompt(updatedPrompt);
  };

  // Toggle individual clinical component in AI synthesizer
  const handleToggleAiComponent = (fixtureId: string) => {
    const nextComponents = selectedAiComponents.includes(fixtureId)
      ? selectedAiComponents.filter(id => id !== fixtureId)
      : [...selectedAiComponents, fixtureId];
    setSelectedAiComponents(nextComponents);
    
    let nextStudentActor = includeStudentActor;
    if (fixtureId === 'candidate') {
      nextStudentActor = !selectedAiComponents.includes('candidate');
      setIncludeStudentActor(nextStudentActor);
    }

    const updatedPrompt = buildSmartClinicalPrompt(
      aiEnvironment,
      selectedScenario?.title,
      nextComponents,
      is360PanoramaMode,
      nextStudentActor
    );
    setAiPrompt(updatedPrompt);
  };

  // Quick preset selector for clinical fixtures
  const applyComponentPreset = (presetKey: 'nurse_patient' | 'ambulation' | 'vitals' | 'hygiene' | 'phlebotomy' | 'exam' | 'patient_care' | 'bed_making' | 'all' | 'none') => {
    let components: string[] = [];
    let studentActor = true;
    if (presetKey === 'nurse_patient') {
      components = ['candidate', 'resident', 'bed', 'overbed_table', 'call_light', 'privacy_curtain', 'sink'];
      studentActor = true;
    } else if (presetKey === 'ambulation') {
      components = ['candidate', 'resident', 'bed', 'wheelchair', 'call_light', 'overbed_table', 'sink'];
      studentActor = true;
    } else if (presetKey === 'vitals') {
      components = ['candidate', 'resident', 'bed', 'vitals_cuff', 'overbed_table', 'call_light'];
      studentActor = true;
    } else if (presetKey === 'hygiene') {
      components = ['candidate', 'sink', 'faucet', 'soap', 'towels', 'waste_bin', 'hygiene_poster'];
      studentActor = true;
    } else if (presetKey === 'phlebotomy') {
      components = ['candidate', 'resident', 'phlebotomy_chair', 'sharps_bin', 'sink'];
      studentActor = true;
    } else if (presetKey === 'exam') {
      components = ['candidate', 'resident', 'exam_table', 'sink', 'call_light'];
      studentActor = true;
    } else if (presetKey === 'patient_care') {
      components = ['candidate', 'resident', 'bed', 'bath_basin', 'overbed_table', 'privacy_curtain', 'sink'];
      studentActor = true;
    } else if (presetKey === 'bed_making') {
      components = ['candidate', 'bed', 'clean_linens', 'overbed_table', 'privacy_curtain'];
      studentActor = true;
    } else if (presetKey === 'all') {
      components = CLINICAL_SKILL_FIXTURES.map(f => f.id);
      studentActor = true;
    } else if (presetKey === 'none') {
      components = [];
      studentActor = false;
    }
    setSelectedAiComponents(components);
    setIncludeStudentActor(studentActor);
    const updatedPrompt = buildSmartClinicalPrompt(
      aiEnvironment,
      selectedScenario?.title,
      components,
      is360PanoramaMode,
      studentActor
    );
    setAiPrompt(updatedPrompt);
  };

  // Generate Photorealistic Image with AI Imagen 3 / Gemini Native
  const handleGenerateAiImage = async () => {
    if (!selectedScenario) return;
    try {
      setIsGeneratingAiImage(true);
      const activeComponentLabels = CLINICAL_SKILL_FIXTURES
        .filter(f => selectedAiComponents.includes(f.id))
        .map(f => f.label);

      const res = await fetch('/practice-exams/api/clinical-sim/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environment: aiEnvironment,
          prompt: aiPrompt || undefined,
          scenarioTitle: selectedScenario.title,
          skillName: selectedScenario.title,
          components: activeComponentLabels,
          selectedComponentIds: selectedAiComponents,
          isPhotosphere3D: is360PanoramaMode,
          includeStudentActor: includeStudentActor
        })
      });
      const data = await res.json();
      if (data.url || data.wixCdnUrl || data.dataUrl) {
        // Prioritize hosted Wix CDN URL or container URL over raw base64 dataUrl
        const resolvedUrl = data.wixCdnUrl || data.url || data.dataUrl;
        setGeneratedAiImageUrl(resolvedUrl);
        if (data.wixCdnUrl) {
          setWixUrlInput(data.wixCdnUrl);
        }
        flashStatus('success', is360PanoramaMode ? 'AI 360° VR Panorama synthesized with selected fixtures' : 'AI photorealistic image synthesized with selected fixtures');
      }
    } catch (err) {
      console.error('Image gen failed:', err);
      flashStatus('error', 'Failed to generate photorealistic image');
    } finally {
      setIsGeneratingAiImage(false);
    }
  };

  // Apply AI image to scenario
  const handleApplyGeneratedImage = async () => {
    if (!generatedAiImageUrl || !selectedScenario) return;
    let imgUrl = generatedAiImageUrl;
    const cleanCertId = (activeCertId || selectedScenario.certId || 'cna').toLowerCase().trim();

    // If image is a raw base64 string, upload to Wix Media first and delete base64 reference!
    if (imgUrl.startsWith('data:image/')) {
      try {
        setIsUploadingToWix(true);
        const fileName = `clinical_${is360PanoramaMode ? '360' : 'env'}_${Date.now()}.jpg`;
        const res = await fetch('/practice-exams/api/clinical-sim/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Data: imgUrl,
            fileName,
            compress: true
          })
        });
        const uploadData = await res.json();
        if (uploadData.wixCdnUrl || uploadData.url) {
          imgUrl = uploadData.wixCdnUrl || uploadData.url;
          setGeneratedAiImageUrl(imgUrl);
          flashStatus('info', '✓ Image uploaded to Wix CDN and base64 reference deleted before applying.');
        }
      } catch (e) {
        console.warn('Auto-upload to Wix skipped during apply:', e);
      } finally {
        setIsUploadingToWix(false);
      }
    }

    const updated: ClinicalSimulatorScenario = {
      ...selectedScenario,
      certId: cleanCertId,
      environment: aiEnvironment as any,
      customImageUrl: imgUrl,
      imageUrl: imgUrl,
      photosphereUrl: imgUrl,
      enable3dPhotosphere: true
    };

    setSelectedScenario(updated);
    setWixUrlInput(imgUrl);
    setPhotosphereUrlInput(imgUrl);

    // Auto-persist immediately so user changes take effect without needing manual save button!
    await persistScenario(updated, is360PanoramaMode 
      ? 'Applied and saved 360° VR Panorama to scenario live.' 
      : 'Applied and saved photorealistic AI image to scenario live.'
    );

    setIsAiImageModalOpen(false);
  };

  // 1-Click State-Board Hand Hygiene Alignment
  const handleApplyHandwashingAlignment = async () => {
    if (!selectedScenario) return;
    const cleanCertId = (activeCertId || selectedScenario.certId || 'cna').toLowerCase().trim();
    const updated: ClinicalSimulatorScenario = {
      ...selectedScenario,
      certId: cleanCertId,
      environment: 'handwashing_station',
      customImageUrl: '',
      imageUrl: '/images/environments/handwashing_station.jpg',
      photosphereUrl: '/images/environments/handwashing_360.jpg',
      hotspots: HANDWASHING_HOTSPOTS_PRESET
    };
    setSelectedScenario(updated);
    setWixUrlInput('');
    setPhotosphereUrlInput('/images/environments/handwashing_360.jpg');
    await persistScenario(updated, 'Aligned scenario to State Board Hand Hygiene Station & Hotspots!');
  };

  // Station & Hotspot immutable updaters with independent 2D & 3D coordinate properties
  const updateStation = (hIdx: number, updates: Partial<SimulatorHotspot>) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const envDef = CLINICAL_ENVIRONMENTS[prev.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i !== hIdx) return hs;
        const def2D = envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
        const def3D = envDef.defaultSphericalCoordinates?.[hs.zone] || coords2DtoSpherical(def2D.x, def2D.y);

        const current2D = (hs.coordinates && typeof hs.coordinates.x === 'number' && typeof hs.coordinates.y === 'number')
          ? { ...hs.coordinates }
          : { x: def2D.x, y: def2D.y };

        const current3D = (hs.sphericalCoordinates && typeof hs.sphericalCoordinates.yaw === 'number' && typeof hs.sphericalCoordinates.pitch === 'number')
          ? { ...hs.sphericalCoordinates }
          : { yaw: def3D.yaw, pitch: def3D.pitch };

        return {
          ...hs,
          ...updates,
          coordinates: updates.coordinates ? { ...updates.coordinates } : current2D,
          sphericalCoordinates: updates.sphericalCoordinates ? { ...updates.sphericalCoordinates } : current3D
        };
      });
      return { ...prev, hotspots: nextHotspots };
    });
  };

  const deleteStation = (hIdx: number) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const nextHotspots = prev.hotspots.filter((_, i) => i !== hIdx);
      return { ...prev, hotspots: nextHotspots };
    });
    setSelectedHotspotIndex(prev => Math.max(0, prev - 1));
  };

  const [isGeneratingStationAi, setIsGeneratingStationAi] = useState(false);

  const addActionToStation = (hIdx: number) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const totalExistingSteps = (prev.hotspots || []).reduce((sum, h) => sum + (h.availableActions || []).length, 0);
      const nextStepNum = totalExistingSteps + 1;
      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i !== hIdx) return hs;
        const newAct: SimulatorHotspotAction = {
          actionId: `act_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          label: `Step ${nextStepNum}: Perform Clinical Skill Step`,
          feedbackDialogue: `Step ${nextStepNum} completed: Candidate executed clinical step according to protocol.`,
          isCritical: false,
          pointsAwarded: 10,
          category: 'technique'
        };
        return {
          ...hs,
          availableActions: [...hs.availableActions, newAct]
        };
      });
      return { ...prev, hotspots: nextHotspots };
    });
  };

  // Re-numbers all action labels sequentially across all stations: Step 1, Step 2, Step 3...
  const handleAutoNumberAllSteps = () => {
    if (!selectedScenario) return;
    let counter = 1;
    const updatedHotspots = (selectedScenario.hotspots || []).map(hs => {
      const updatedActions = (hs.availableActions || []).map(act => {
        const strippedLabel = act.label.replace(/^step\s*\d+[:\s-]*/i, '').trim();
        const newLabel = `Step ${counter}: ${strippedLabel || 'Perform Clinical Action'}`;
        const strippedFeedback = (act.feedbackDialogue || '').replace(/^step\s*\d+\s*(completed|verified)?[:\s-]*/i, '').trim();
        const newFeedback = `Step ${counter} completed: ${strippedFeedback || 'Action verified according to protocol.'}`;
        counter++;
        return {
          ...act,
          label: newLabel,
          feedbackDialogue: newFeedback
        };
      });
      return { ...hs, availableActions: updatedActions };
    });

    const updated = { ...selectedScenario, hotspots: updatedHotspots };
    setSelectedScenario(updated);
    flashStatus('success', `Chronologically auto-numbered ${counter - 1} steps across all stations (Step 1 to Step ${counter - 1})!`);
  };

  // Generates numbered procedural steps for the selected station using AI
  const handleGenerateStationActionsWithAi = async (hIdx: number) => {
    if (!selectedScenario) return;
    const targetStation = selectedScenario.hotspots[hIdx];
    if (!targetStation) return;

    // Calculate starting step number based on preceding stations
    let priorStepsCount = 0;
    for (let i = 0; i < hIdx; i++) {
      priorStepsCount += (selectedScenario.hotspots[i].availableActions || []).length;
    }
    const startingStepNum = priorStepsCount + 1;

    setIsGeneratingStationAi(true);
    try {
      const newActions = await generateStationActionsWithAi(
        selectedScenario.title,
        targetStation.name,
        targetStation.zone,
        startingStepNum
      );

      setSelectedScenario(prev => {
        if (!prev) return prev;
        const nextHotspots = prev.hotspots.map((hs, i) => {
          if (i !== hIdx) return hs;
          return {
            ...hs,
            availableActions: [...hs.availableActions, ...newActions]
          };
        });
        return { ...prev, hotspots: nextHotspots };
      });

      flashStatus('success', `AI generated ${newActions.length} numbered steps for "${targetStation.name}"!`);
    } catch (err: any) {
      flashStatus('error', 'Failed to generate station steps with AI');
    } finally {
      setIsGeneratingStationAi(false);
    }
  };

  const updateStationAction = (hIdx: number, aIdx: number, updates: Partial<SimulatorHotspotAction>) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i !== hIdx) return hs;
        const nextActions = hs.availableActions.map((act, j) => {
          if (j !== aIdx) return act;
          return { ...act, ...updates };
        });
        return { ...hs, availableActions: nextActions };
      });
      return { ...prev, hotspots: nextHotspots };
    });
  };

  const deleteStationAction = (hIdx: number, aIdx: number) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i !== hIdx) return hs;
        return {
          ...hs,
          availableActions: hs.availableActions.filter((_, j) => j !== aIdx)
        };
      });
      return { ...prev, hotspots: nextHotspots };
    });
  };

  // Re-orders actions within the same Station / Room Fixture (Up / Down)
  const moveStationAction = (hIdx: number, aIdx: number, direction: 'up' | 'down') => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const targetStation = prev.hotspots[hIdx];
      if (!targetStation || !targetStation.availableActions) return prev;

      const actions = [...targetStation.availableActions];
      const targetIdx = direction === 'up' ? aIdx - 1 : aIdx + 1;
      if (targetIdx < 0 || targetIdx >= actions.length) return prev;

      const [moved] = actions.splice(aIdx, 1);
      actions.splice(targetIdx, 0, moved);

      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i !== hIdx) return hs;
        return { ...hs, availableActions: actions };
      });
      return { ...prev, hotspots: nextHotspots };
    });
  };

  // Moves a Station Clinical Action from one Station / Room Fixture to another Station / Room Fixture
  const moveActionToStation = (sourceHIdx: number, aIdx: number, targetHIdx: number) => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      if (sourceHIdx === targetHIdx) return prev;
      const sourceStation = prev.hotspots[sourceHIdx];
      const targetStation = prev.hotspots[targetHIdx];
      if (!sourceStation || !targetStation) return prev;

      const actionToMove = sourceStation.availableActions[aIdx];
      if (!actionToMove) return prev;

      const newSourceActions = sourceStation.availableActions.filter((_, j) => j !== aIdx);
      const newTargetActions = [...(targetStation.availableActions || []), actionToMove];

      const nextHotspots = prev.hotspots.map((hs, i) => {
        if (i === sourceHIdx) return { ...hs, availableActions: newSourceActions };
        if (i === targetHIdx) return { ...hs, availableActions: newTargetActions };
        return hs;
      });

      flashStatus('success', `Moved "${actionToMove.label || 'Action'}" to station "${targetStation.name || `Station #${targetHIdx + 1}`}".`);
      return { ...prev, hotspots: nextHotspots };
    });
  };

  // Re-orders Station Name / Room Fixtures in the scenario
  const moveStation = (hIdx: number, direction: 'up' | 'down') => {
    setSelectedScenario(prev => {
      if (!prev) return prev;
      const targetIdx = direction === 'up' ? hIdx - 1 : hIdx + 1;
      if (targetIdx < 0 || targetIdx >= prev.hotspots.length) return prev;
      const hotspots = [...prev.hotspots];
      const [moved] = hotspots.splice(hIdx, 1);
      hotspots.splice(targetIdx, 0, moved);
      return { ...prev, hotspots };
    });
    setSelectedHotspotIndex(prev => {
      if (prev === hIdx) {
        return direction === 'up' ? prev - 1 : prev + 1;
      }
      return prev;
    });
  };

  const isCertEnabled = enabledCerts.includes(activeCertId);

  return (
    <div id="clinical-simulator-studio" className="min-h-screen pb-16 flex flex-col gap-6">
      {/* Top Studio Header */}
      <div id="studio-header-card" className={`${theme.cardBg} p-5 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-800`}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-700 dark:text-purple-300 text-[10px] font-extrabold uppercase tracking-wider border border-purple-300 dark:border-purple-800">
                Super Admin Feature
              </span>
              <span className="text-xs text-slate-500">•</span>
              <span className="text-xs text-slate-600 dark:text-slate-400 font-semibold">
                AI-Assisted Clinical Simulator Studio & Delivery Engine
              </span>
            </div>
            <h1 className={`text-2xl font-black text-slate-900 dark:text-slate-100 ${theme.headerFont}`}>
              Clinical Simulator Studio
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
              Author, AI-generate, test in sandbox, and publish high-impact interactive clinical simulation scenarios across all certification programs.
            </p>
          </div>

          {/* Master Feature Flag Switches */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Global Switch */}
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-slate-100">Global Simulator</span>
                <span className="text-[11px] text-slate-500">{isGlobalSimulatorEnabled ? "Live Platform-wide" : "Turned Off"}</span>
              </div>
              <button
                id="toggle-global-simulator-btn"
                type="button"
                onClick={handleToggleGlobalFlag}
                disabled={isUpdatingFlag}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isGlobalSimulatorEnabled ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isGlobalSimulatorEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Cert Switch */}
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-slate-100">{activeCertId.toUpperCase()} Access</span>
                <span className="text-[11px] text-slate-500">{isCertEnabled ? "Enabled for Students" : "Disabled"}</span>
              </div>
              <button
                id="toggle-cert-simulator-btn"
                type="button"
                onClick={() => handleToggleCertFlag(activeCertId)}
                disabled={isUpdatingFlag}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isCertEnabled ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isCertEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Status Alert Banner */}
        {statusMessage && (
          <div className={`mt-4 p-3 rounded-xl text-xs font-semibold flex items-center justify-between border ${
            statusMessage.type === 'success' 
              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800' 
              : statusMessage.type === 'error'
              ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 border-rose-300 dark:border-rose-800'
              : 'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-800'
          }`}>
            <span>{statusMessage.text}</span>
            <button type="button" onClick={() => setStatusMessage(null)} className="opacity-70 hover:opacity-100">✕</button>
          </div>
        )}
      </div>

      {/* Certification Navigation Tabs */}
      <div id="cert-navigation-pills" className="flex items-center gap-2 overflow-x-auto pb-1">
        {certs.map(cert => {
          const isActive = cert.id === activeCertId;
          const isCurrentCertEnabled = enabledCerts.includes(cert.id);

          return (
            <button
              key={cert.id}
              id={`cert-tab-${cert.id}`}
              type="button"
              onClick={() => {
                setActiveCertId(cert.id);
                localStorage.setItem('cna_admin_active_cert', cert.id);
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-2 border ${
                isActive 
                  ? 'bg-blue-600 text-white border-blue-600 shadow-xs' 
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400'
              }`}
            >
              <span>{cert.shortName || cert.name}</span>
              <span className={`w-2 h-2 rounded-full ${isCurrentCertEnabled ? 'bg-emerald-400' : 'bg-slate-400'}`} />
            </button>
          );
        })}
      </div>

      {/* Main Studio Split Layout: Sidebar Scenarios List + Editor/Sandbox Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Scenarios List & Actions */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div id="scenarios-list-card" className={`${theme.cardBg} p-4 sm:p-5 shadow-xs border border-slate-200 dark:border-slate-800`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-sm font-bold text-slate-900 dark:text-slate-100 ${theme.headerFont}`}>
                  Scenarios ({scenarios.length})
                </h3>
                <span className="text-[11px] text-slate-500">
                  Certification: {activeCertId.toUpperCase()}
                </span>
              </div>
              <button
                id="create-new-scenario-btn"
                type="button"
                onClick={handleCreateNewScenario}
                className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1.5 transition-all shadow-xs active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New</span>
              </button>
            </div>

            {scenarios.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                No clinical scenarios configured for this certification yet. Click "+ New" or generate with AI.
              </div>
            ) : (
              <div className="flex flex-col gap-2.5 max-h-[560px] overflow-y-auto pr-1">
                {scenarios.map(sc => {
                  const isSelected = selectedScenario?.id === sc.id;
                  const attachedSkill = availableSkills.find(s => s.id === sc.skillId);

                  return (
                    <div
                      key={sc.id}
                      id={`scenario-item-${sc.id}`}
                      onClick={() => {
                        setSelectedScenario(sc);
                        setWixUrlInput(sc.customImageUrl || (sc.imageUrl && !sc.imageUrl.startsWith('/images/environments/') ? sc.imageUrl : ''));
                        setPhotosphereUrlInput(sc.photosphereUrl || '');
                        setAiEnvironment(sc.environment || 'bedside_room');
                        localStorage.setItem(`cna_last_active_sim_${activeCertId}`, sc.id);
                      }}
                      className={`p-3.5 rounded-xl cursor-pointer transition-all border flex flex-col gap-1.5 ${
                        isSelected 
                          ? 'bg-blue-50/70 dark:bg-blue-950/40 border-blue-500 shadow-xs' 
                          : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                          {sc.title}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                          sc.status === 'published' 
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800' 
                            : sc.status === 'testing'
                            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800'
                            : 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700'
                        }`}>
                          {sc.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span className="line-clamp-1">
                          Skill: {attachedSkill?.name || sc.skillId || 'General'}
                        </span>
                        <span>{(sc.hotspots || []).length} stations</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Scenario Studio Workspace (Editor & Sandbox) */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          {selectedScenario ? (
            <div id="scenario-workspace-card" className={`${theme.cardBg} p-5 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col gap-5`}>
              {/* Studio Workspace Header Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800 gap-3">
                {/* Switch between Editor and Sandbox */}
                <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 self-start">
                  <button
                    id="tab-switch-editor-btn"
                    type="button"
                    onClick={() => setActiveTab('editor')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                      activeTab === 'editor' 
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs' 
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Scenario Authoring</span>
                  </button>
                  <button
                    id="tab-switch-sandbox-btn"
                    type="button"
                    onClick={() => setActiveTab('sandbox')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                      activeTab === 'sandbox' 
                        ? 'bg-blue-600 text-white shadow-xs' 
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    <Play className="w-3.5 h-3.5 fill-current" />
                    <span>Test in Sandbox</span>
                  </button>
                </div>

                {/* Primary Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    id="ai-generate-scenario-btn"
                    type="button"
                    onClick={handleGenerateWithAi}
                    disabled={isGeneratingAi}
                    className="px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 font-bold text-xs flex items-center gap-1.5 hover:bg-purple-100 transition-all active:scale-95 shadow-2xs"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                    <span>{isGeneratingAi ? 'Synthesizing...' : 'Generate with AI'}</span>
                  </button>

                  <button
                    id="save-scenario-btn"
                    type="button"
                    onClick={handleSaveScenario}
                    className={`px-4 py-1.5 rounded-xl ${theme.primaryBtn} text-xs font-bold flex items-center gap-1.5 shadow-xs`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    <span>Save Scenario</span>
                  </button>

                  <button
                    id="delete-scenario-btn"
                    type="button"
                    onClick={() => handleDeleteScenario(selectedScenario.id)}
                    className="p-2 rounded-xl text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                    title="Delete scenario"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* VIEW 1: Embedded Admin Test Sandbox */}
              {activeTab === 'sandbox' ? (
                <div id="admin-sandbox-view" className="flex flex-col gap-4">
                  <div className="p-3.5 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-xs text-blue-900 dark:text-blue-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-blue-600" />
                      <span><strong>Testing Sandbox Active:</strong> You can test clinical workflows, trigger intentional board traps, and verify passing score thresholds before publishing.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveTab('editor')}
                      className="font-bold underline text-blue-700 dark:text-blue-300"
                    >
                      Return to Editor
                    </button>
                  </div>

                  <ClinicalRoomStage
                    scenario={selectedScenario}
                    isAdminTestingMode={true}
                    onExit={() => setActiveTab('editor')}
                    onScenarioChange={(updated) => setSelectedScenario(updated)}
                  />
                </div>
              ) : (
                /* VIEW 2: Scenario Authoring Editor */
                <div id="admin-editor-view" className="flex flex-col gap-5">
                  {/* Prominent Live Student Access & Publication Status Bar */}
                  <div className={`p-4 rounded-2xl border flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs ${
                    selectedScenario.status === 'published'
                      ? 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800'
                      : 'bg-amber-50/80 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800'
                  }`}>
                    <div className="flex items-start md:items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-xs ${
                        selectedScenario.status === 'published' 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-amber-500 text-slate-950'
                      }`}>
                        {selectedScenario.status === 'published' ? <Check className="w-5 h-5 stroke-[2.5]" /> : <Eye className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-slate-100">
                            Student Lab Availability:
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            selectedScenario.status === 'published'
                              ? 'bg-emerald-600 text-white shadow-2xs'
                              : 'bg-amber-500 text-slate-950 shadow-2xs'
                          }`}>
                            {selectedScenario.status === 'published' ? '● LIVE TO STUDENTS' : '○ DRAFT (Hidden from Students)'}
                          </span>
                          {selectedScenario.enable3dPhotosphere && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-600 text-white flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> 360° Photosphere Active
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 leading-snug">
                          {selectedScenario.status === 'published' ? (
                            <>
                              Students can access this simulation in the <strong>Skills Lab</strong> under procedural skill: <span className="font-extrabold text-emerald-800 dark:text-emerald-300">{availableSkills.find(s => s.id === selectedScenario.skillId)?.name || 'Direct Link / All Skills'}</span> (Tab: <strong>Clinical Simulator 360° VR</strong>).
                            </>
                          ) : (
                            <>
                              This scenario is currently in <strong>Draft mode</strong> (hidden from non-admin users). To enable it for students in the Skills Lab, click <strong>"Publish Live for Students"</strong>.
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                      {selectedScenario.status !== 'published' ? (
                        <button
                          type="button"
                          id="publish-live-for-students-btn"
                          onClick={() => {
                            persistScenario(
                              { ...selectedScenario, status: 'published', enable3dPhotosphere: true },
                              'Scenario is now LIVE for students in the Skills Lab & saved to database!'
                            );
                          }}
                          className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider shadow-sm flex items-center gap-1.5 transition-all active:scale-95 cursor-pointer"
                        >
                          <Check className="w-4 h-4" />
                          <span>Publish Live for Students</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          id="unpublish-scenario-btn"
                          onClick={() => {
                            persistScenario(
                              { ...selectedScenario, status: 'draft' },
                              'Scenario set to Draft mode & saved (hidden from students).'
                            );
                          }}
                          className="px-3.5 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-800 dark:text-slate-200 text-xs font-bold transition-all cursor-pointer"
                        >
                          Unpublish (Set to Draft)
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Editor Sub-Tabs */}
                  <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setEditorSubTab('overview')}
                      className={`pb-2 px-1 border-b-2 transition-colors ${
                        editorSubTab === 'overview' 
                          ? 'border-blue-600 text-blue-600 font-bold' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Overview & Skill Link
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorSubTab('patient')}
                      className={`pb-2 px-1 border-b-2 transition-colors ${
                        editorSubTab === 'patient' 
                          ? 'border-blue-600 text-blue-600 font-bold' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Patient Profile & Vitals
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorSubTab('hotspots')}
                      className={`pb-2 px-1 border-b-2 transition-colors ${
                        editorSubTab === 'hotspots' 
                          ? 'border-blue-600 text-blue-600 font-bold' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Stations & Actions ({(selectedScenario.hotspots || []).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorSubTab('traps')}
                      className={`pb-2 px-1 border-b-2 transition-colors ${
                        editorSubTab === 'traps' 
                          ? 'border-blue-600 text-blue-600 font-bold' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      State Board Traps ({(selectedScenario.instantFailTraps || []).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorSubTab('rubric')}
                      className={`pb-2 px-1 border-b-2 transition-colors ${
                        editorSubTab === 'rubric' 
                          ? 'border-blue-600 text-blue-600 font-bold' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Rubric Milestone Weights
                    </button>
                  </div>

                  {/* Sub-Tab 1: Overview & Skill Link */}
                  {editorSubTab === 'overview' && (
                    <div className="flex flex-col gap-4 text-xs">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                            Scenario Title
                          </label>
                          <input
                            id="edit-scenario-title"
                            type="text"
                            value={selectedScenario.title}
                            onChange={(e) => setSelectedScenario({ ...selectedScenario, title: e.target.value })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                            Attach to Existing Procedural Skill
                          </label>
                          <select
                            id="edit-scenario-skill-link"
                            value={selectedScenario.skillId}
                            onChange={(e) => {
                              const newSkillId = e.target.value;
                              const attachedSkill = availableSkills.find(s => s.id === newSkillId);
                              if (attachedSkill) {
                                const preset = resolveSkillEnvironmentPreset(attachedSkill.name);
                                const isCustomUserImage = Boolean(selectedScenario.customImageUrl && !selectedScenario.customImageUrl.startsWith('/images/environments/'));
                                const updated = {
                                  ...selectedScenario,
                                  skillId: newSkillId,
                                  title: (selectedScenario.title.startsWith('New ') || !selectedScenario.title) ? `${attachedSkill.name} - Clinical Simulation` : selectedScenario.title,
                                  environment: (selectedScenario.environment === 'bedside_room' && preset.environment !== 'bedside_room') ? preset.environment as any : selectedScenario.environment,
                                  imageUrl: isCustomUserImage ? selectedScenario.imageUrl : preset.imageUrl,
                                  photosphereUrl: isCustomUserImage ? selectedScenario.photosphereUrl : preset.photosphereUrl,
                                  enable3dPhotosphere: true
                                };
                                setSelectedScenario(updated);
                                if (!isCustomUserImage) {
                                  setWixUrlInput(preset.imageUrl);
                                  setPhotosphereUrlInput(preset.photosphereUrl);
                                }
                                flashStatus('success', `Scenario & 360 stage automatically aligned with skill: "${attachedSkill.name}".`);
                              } else {
                                setSelectedScenario({ ...selectedScenario, skillId: '' });
                              }
                            }}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          >
                            <option value="">-- No Direct Skill Link (Standalone) --</option>
                            {availableSkills.map(sk => (
                              <option key={sk.id} value={sk.id}>{sk.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                            Publishing Lifecycle Status
                          </label>
                          <select
                            id="edit-scenario-status"
                            value={selectedScenario.status}
                            onChange={(e) => setSelectedScenario({ ...selectedScenario, status: e.target.value as any })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          >
                            <option value="draft">Draft (Admin Only)</option>
                            <option value="testing">Testing (Admin Only Sandbox)</option>
                            <option value="published">Published (Live to Students)</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                            Clinical Environment Stage
                          </label>
                          <select
                            id="edit-scenario-environment"
                            value={selectedScenario.environment}
                            onChange={(e) => {
                              const newEnv = e.target.value;
                              const envInfo = CLINICAL_ENVIRONMENTS[newEnv];
                              setSelectedScenario({ 
                                ...selectedScenario, 
                                environment: newEnv as any,
                                imageUrl: envInfo?.imageUrl || selectedScenario.imageUrl,
                                photosphereUrl: envInfo?.photosphereUrl || selectedScenario.photosphereUrl,
                                customImageUrl: ''
                              });
                              setWixUrlInput('');
                              setPhotosphereUrlInput(envInfo?.photosphereUrl || '');
                            }}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          >
                            <option value="handwashing_station">Hand Hygiene & Sink Station (State Board Standard)</option>
                            <option value="bedside_room">Bedside Hospital/Care Room</option>
                            <option value="exam_room">Outpatient Examination Room</option>
                            <option value="phlebotomy_chair">Phlebotomy Draw Bay</option>
                            <option value="icu_bay">Intensive / Acute Care Bay</option>
                          </select>
                        </div>
                      </div>

                      {/* Visual Environment Cards */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-slate-700 dark:text-slate-300">
                            Environment Stage Preview
                          </span>
                          {selectedScenario.environment === 'handwashing_station' && (
                            <button
                              type="button"
                              onClick={handleApplyHandwashingAlignment}
                              className="text-[11px] text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1 cursor-pointer"
                            >
                              <Sparkles className="w-3 h-3" />
                              <span>Auto-Align Hand Hygiene Hotspots</span>
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                          {Object.entries(CLINICAL_ENVIRONMENTS).map(([envKey, envConfig]) => {
                            const isSelected = selectedScenario.environment === envKey;
                            return (
                              <button
                                key={envKey}
                                type="button"
                                onClick={() => {
                                  const envInfo = CLINICAL_ENVIRONMENTS[envKey];
                                  setSelectedScenario({ 
                                    ...selectedScenario, 
                                    environment: envKey as any,
                                    imageUrl: envInfo?.imageUrl || selectedScenario.imageUrl,
                                    photosphereUrl: envInfo?.photosphereUrl || selectedScenario.photosphereUrl,
                                    customImageUrl: ''
                                  });
                                  setWixUrlInput('');
                                  setPhotosphereUrlInput(envInfo?.photosphereUrl || '');
                                }}
                                className={`flex flex-col text-left rounded-xl overflow-hidden border-2 transition-all p-1.5 ${
                                  isSelected
                                    ? 'border-blue-600 bg-blue-50/50 dark:bg-blue-950/40 ring-2 ring-blue-500/20'
                                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white dark:bg-slate-800'
                                }`}
                              >
                                <div className="w-full aspect-16/9 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-900 mb-1.5 relative">
                                  <img 
                                    src={envConfig.image} 
                                    alt={envConfig.name}
                                    className="w-full h-full object-cover" 
                                  />
                                  {isSelected && (
                                    <div className="absolute top-1 right-1 p-0.5 rounded-full bg-blue-600 text-white shadow-xs">
                                      <Check className="w-3 h-3" />
                                    </div>
                                  )}
                                </div>
                                <span className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate">
                                  {envConfig.name}
                                </span>
                                <span className="text-[10px] text-slate-500 line-clamp-1">
                                  {envConfig.setting}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Photorealistic Environmental Imagery & Wix Hosting */}
                      <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <FileImage className="w-4 h-4 text-blue-600" />
                              <span className="font-bold text-slate-900 dark:text-slate-100 text-xs">
                                Photorealistic Stage Imagery & Hosting
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Generate with AI, download high-res image to host on your Wix CDN, or link any external image URL.
                            </p>
                          </div>

                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={handleOpenAiImageModal}
                              className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-2xs transition-all"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>Generate with AI</span>
                            </button>

                            <button
                              type="button"
                              onClick={handleDownloadActiveImage}
                              className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 font-bold text-xs flex items-center gap-1.5 border border-slate-300 dark:border-slate-600 shadow-2xs transition-all"
                              title="Download photorealistic image to upload to Wix CDN"
                            >
                              <Download className="w-3.5 h-3.5 text-blue-600" />
                              <span>Download for Wix</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleUploadGeneratedImageToWix()}
                              disabled={isUploadingToWix}
                              className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-2xs transition-all cursor-pointer disabled:opacity-50"
                              title="Upload active stage image directly to Wix Media Manager (automatically deletes base64 references)"
                            >
                              {isUploadingToWix ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}
                              <span>{isUploadingToWix ? 'Uploading to Wix...' : 'Upload to Wix Media'}</span>
                            </button>

                            <input
                              type="file"
                              ref={studioFileInputRef}
                              onChange={handleStudioFileUpload}
                              accept="image/*"
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => studioFileInputRef.current?.click()}
                              className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 font-bold text-xs flex items-center gap-1.5 border border-slate-300 dark:border-slate-600 shadow-2xs transition-all"
                            >
                              <Upload className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Upload File</span>
                            </button>
                          </div>
                        </div>

                        {uploadStatus && (
                          <div className="text-[11px] font-bold text-blue-600 animate-pulse">
                            {uploadStatus}
                          </div>
                        )}

                        {/* Wix CDN URL Input Bar */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                              <Globe className="w-3.5 h-3.5" />
                            </div>
                            <input
                              type="url"
                              value={wixUrlInput}
                              onChange={(e) => setWixUrlInput(e.target.value)}
                              placeholder="Wix CDN URL (e.g. https://static.wixstatic.com/media/...) or custom image URL"
                              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={handleApplyWixUrl}
                              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition-colors"
                            >
                              Apply URL
                            </button>

                            {(selectedScenario.customImageUrl || selectedScenario.imageUrl) && (
                              <button
                                type="button"
                                onClick={handleClearCustomUrl}
                                className="px-2.5 py-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:text-rose-600 text-xs font-semibold"
                              >
                                Revert to Default
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Image Preview & Current Source Badge */}
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                          <div className="w-16 h-10 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                            <img
                              src={
                                selectedScenario.customImageUrl ||
                                selectedScenario.imageUrl ||
                                CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room']?.imageUrl ||
                                '/images/environments/bedside_room.jpg'
                              }
                              alt="Active room preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src = '/images/environments/bedside_room.jpg';
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                Active Room Imagery:
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                selectedScenario.customImageUrl?.includes('wixstatic.com') || selectedScenario.imageUrl?.includes('wixstatic.com')
                                  ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-300'
                                  : (selectedScenario.customImageUrl || selectedScenario.imageUrl)
                                  ? 'bg-purple-500/10 text-purple-600 border border-purple-300'
                                  : 'bg-slate-100 text-slate-600 border border-slate-300'
                              }`}>
                                {selectedScenario.customImageUrl?.includes('wixstatic.com') || selectedScenario.imageUrl?.includes('wixstatic.com')
                                  ? 'Hosted on Wix CDN'
                                  : (selectedScenario.customImageUrl || selectedScenario.imageUrl)
                                  ? 'Custom / AI Hosted'
                                  : 'Environmental Preset'}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400 truncate font-mono mt-0.5">
                              {selectedScenario.customImageUrl || selectedScenario.imageUrl || 'Built-in environmental asset'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 360° 3D Photosphere Stage (Option A) Master Super Admin Control */}
                      <div className="p-4 rounded-xl border-2 border-purple-300 dark:border-purple-800 bg-gradient-to-br from-purple-50/70 via-indigo-50/40 to-slate-50 dark:from-purple-950/30 dark:via-indigo-950/20 dark:to-slate-900/40 flex flex-col gap-3 shadow-xs">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              <span className="font-bold text-slate-900 dark:text-slate-100 text-xs">
                                360° 3D Photosphere Stage (Option A)
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/60 dark:text-purple-300 border border-purple-300 dark:border-purple-700">
                                Super Admin Control
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Enables students to explore an authentic 360° spherical room using mouse or touch. Hotspots are projected in spherical 3D space (Yaw & Pitch).
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer select-none bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-purple-200 dark:border-purple-700 shadow-2xs">
                              <input
                                type="checkbox"
                                id="toggle-3d-photosphere-master"
                                checked={selectedScenario.enable3dPhotosphere || false}
                                onChange={(e) => {
                                  persistScenario(
                                    { ...selectedScenario, enable3dPhotosphere: e.target.checked },
                                    e.target.checked ? '360° 3D Photosphere stage enabled & saved!' : '360° Photosphere disabled & saved!'
                                  );
                                }}
                                className="w-4 h-4 text-purple-600 rounded cursor-pointer"
                              />
                              <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                {selectedScenario.enable3dPhotosphere ? '3D Stage Enabled' : '3D Stage Disabled'}
                              </span>
                            </label>

                            <button
                              type="button"
                              onClick={() => setActiveTab('sandbox')}
                              className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1 shadow-2xs cursor-pointer"
                              title="Launch sandbox to test 3D rotation and hotspot interactions"
                            >
                              <Play className="w-3.5 h-3.5" />
                              <span>Test in Sandbox</span>
                            </button>
                          </div>
                        </div>

                        {/* Photosphere Panorama Source Bar */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-purple-200 dark:border-purple-800/60">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">
                              360° Equirectangular Panorama Image:
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="url"
                                value={photosphereUrlInput}
                                onChange={(e) => setPhotosphereUrlInput(e.target.value)}
                                placeholder="e.g. /images/environments/handwashing_360.jpg or Wix CDN URL"
                                className="flex-1 p-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (!photosphereUrlInput) return;
                                  setSelectedScenario({
                                    ...selectedScenario,
                                    photosphereUrl: photosphereUrlInput,
                                    enable3dPhotosphere: true
                                  });
                                  flashStatus('success', 'Saved 360° panorama URL to scenario');
                                }}
                                className="px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                              >
                                Apply 360 URL
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">
                              Curated Clinical 360° Presets:
                            </label>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => {
                                  const presetUrl = '/images/environments/handwashing_360.jpg';
                                  setPhotosphereUrlInput(presetUrl);
                                  setSelectedScenario({
                                    ...selectedScenario,
                                    photosphereUrl: presetUrl,
                                    enable3dPhotosphere: true
                                  });
                                  flashStatus('success', 'Applied 360° Clinical Hand Hygiene Scrub Room');
                                }}
                                className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 hover:bg-purple-50 text-purple-900 dark:text-purple-200 font-bold text-[11px] cursor-pointer"
                              >
                                Hand Hygiene 360° Scrub Room
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const presetUrl = '/images/environments/bedside_room_360.jpg';
                                  setPhotosphereUrlInput(presetUrl);
                                  setSelectedScenario({
                                    ...selectedScenario,
                                    photosphereUrl: presetUrl,
                                    enable3dPhotosphere: true
                                  });
                                  flashStatus('success', 'Applied 360° Hospital Bedside Patient Room');
                                }}
                                className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 hover:bg-purple-50 text-purple-900 dark:text-purple-200 font-bold text-[11px] cursor-pointer"
                              >
                                Bedside Room 360°
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Skill-Tied Safety & Room State Controls Configuration */}
                      <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-850 flex flex-col gap-2.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <div className="flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-bold text-slate-800 dark:text-slate-200 text-xs">
                              Skill-Tied Safety & Bedside State Action Buttons
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-500">
                            Only enabled buttons appear on the student simulator canvas
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 leading-tight">
                          Select which quick state tags are applicable to this clinical skill. Disabling unneeded buttons prevents candidates from being misled by steps not part of the current procedure rubric.
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 pt-1">
                          {[
                            { key: 'bed_lock', label: 'Bed Brakes Locked', desc: 'Bed caster locks' },
                            { key: 'hands_clean', label: 'Hand Hygiene', desc: 'Wash/Sanitize hands' },
                            { key: 'call_light', label: 'Call Light', desc: 'Within resident reach' },
                            { key: 'curtain', label: 'Curtain Privacy', desc: 'Privacy curtain drawn' },
                            { key: 'shoes', label: 'Footwear Safe', desc: 'Non-skid safety footwear' },
                            { key: 'gloves', label: 'Gloves Applied', desc: 'Clean examination gloves' }
                          ].map(tag => {
                            const isIncluded = (selectedScenario.activeSafetyTags || ['bed_lock', 'hands_clean', 'call_light', 'curtain', 'shoes']).includes(tag.key);
                            return (
                              <button
                                key={tag.key}
                                type="button"
                                onClick={() => {
                                  const currentTags = selectedScenario.activeSafetyTags || ['bed_lock', 'hands_clean', 'call_light', 'curtain', 'shoes'];
                                  const nextTags = isIncluded
                                    ? currentTags.filter(t => t !== tag.key)
                                    : [...currentTags, tag.key];
                                  setSelectedScenario({ ...selectedScenario, activeSafetyTags: nextTags });
                                }}
                                className={`p-2.5 rounded-lg border text-left flex flex-col gap-1 transition-all cursor-pointer ${
                                  isIncluded
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-[11px]">{tag.label}</span>
                                  <span className={`w-2 h-2 rounded-full ${isIncluded ? 'bg-white' : 'bg-slate-300 dark:bg-slate-600'}`} />
                                </div>
                                <span className={`text-[9.5px] leading-tight ${isIncluded ? 'text-blue-100' : 'text-slate-500'}`}>
                                  {tag.desc}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                          Scenario Clinical Description & Candidate Briefing
                        </label>
                        <textarea
                          id="edit-scenario-description"
                          rows={3}
                          value={selectedScenario.description}
                          onChange={(e) => setSelectedScenario({ ...selectedScenario, description: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium leading-relaxed"
                        />
                      </div>
                    </div>
                  )}

                  {/* Sub-Tab 2: Patient Profile */}
                  {editorSubTab === 'patient' && (
                    <div className="flex flex-col gap-4 text-xs">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Patient Name</label>
                          <input
                            type="text"
                            value={selectedScenario.patient?.name || ''}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              patient: { ...(selectedScenario.patient || {}), name: e.target.value } as any
                            })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Age</label>
                          <input
                            type="number"
                            value={selectedScenario.patient?.age ?? 72}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              patient: { ...(selectedScenario.patient || {}), age: parseInt(e.target.value) || 0 } as any
                            })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Room #</label>
                          <input
                            type="text"
                            value={selectedScenario.patient?.roomNumber || ''}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              patient: { ...(selectedScenario.patient || {}), roomNumber: e.target.value } as any
                            })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Primary Diagnosis</label>
                          <input
                            type="text"
                            value={selectedScenario.patient?.primaryDiagnosis || ''}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              patient: { ...(selectedScenario.patient || {}), primaryDiagnosis: e.target.value } as any
                            })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Mobility Status</label>
                          <input
                            type="text"
                            value={selectedScenario.patient?.mobilityStatus || ''}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              patient: { ...(selectedScenario.patient || {}), mobilityStatus: e.target.value } as any
                            })}
                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                          Clinical Precautions (Comma-separated)
                        </label>
                        <input
                          type="text"
                          value={(selectedScenario.patient?.precautions || []).join(', ')}
                          onChange={(e) => setSelectedScenario({
                            ...selectedScenario,
                            patient: {
                              ...(selectedScenario.patient || {}),
                              precautions: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                            } as any
                          })}
                          className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                          placeholder="e.g. High Fall Risk, Non-Skid Footwear Mandatory"
                        />
                      </div>
                    </div>
                  )}

                  {/* Sub-Tab 3: Hotspots & Actions */}
                  {editorSubTab === 'hotspots' && (
                    <div className="flex flex-col gap-4 text-xs">
                      {/* Interactive Visual Hotspot Pin Placer */}
                      <div id="studio-visual-pin-placer" className="flex flex-col gap-2.5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xs">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2.5 pb-1 border-b border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-2">
                            <Crosshair className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                              Interactive Hotspot Pin Placer
                            </span>
                            <span className="text-[11px] text-slate-500 hidden sm:inline">
                              — 2D Flat Canvas & 3D VR Photosphere maintain completely independent pin coordinates
                            </span>
                          </div>

                          {/* Station Attachment Picker & Independent Coordinate Chips */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-600 dark:text-slate-400 text-xs">Attached Station:</span>
                              <select
                                value={selectedHotspotIndex}
                                onChange={(e) => setSelectedHotspotIndex(Number(e.target.value))}
                                className="p-1 px-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-850 text-slate-900 dark:text-slate-100 font-bold text-xs shadow-2xs focus:ring-2 focus:ring-blue-500"
                              >
                                {selectedScenario.hotspots.map((hs, i) => (
                                  <option key={hs.id} value={i}>
                                    #{i + 1}: {hs.name} ({hs.zone})
                                  </option>
                                ))}
                              </select>
                            </div>

                            {selectedScenario.hotspots[selectedHotspotIndex] && (() => {
                              const activeHs = selectedScenario.hotspots[selectedHotspotIndex];
                              const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                              const c2d = activeHs.coordinates || envDef.defaultHotspotCoordinates[activeHs.zone] || { x: 50, y: 50 };
                              const c3d = activeHs.sphericalCoordinates || envDef.defaultSphericalCoordinates?.[activeHs.zone] || coords2DtoSpherical(c2d.x, c2d.y);
                              return (
                                <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
                                  <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 font-mono font-bold" title="2D Flat Canvas Pin Position">
                                    2D: X:{c2d.x}% Y:{c2d.y}%
                                  </span>
                                  <span className="px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 font-mono font-bold" title="360° 3D VR Spherical Coordinates">
                                    3D: Yaw:{c3d.yaw}° Pitch:{c3d.pitch}°
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Visual Placer Mode Toggle: 2D Flat Canvas vs 360 3D VR Photosphere vs Split View */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 dark:bg-slate-800/60 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                              Active Placer Canvas:
                            </span>
                            <span className="text-[11px] text-slate-500">
                              {studioPlacerMode === '2d' && '(Click canvas to position 2D pin)'}
                              {studioPlacerMode === '3d' && '(Rotate & click to position 3D VR pin)'}
                              {studioPlacerMode === 'split' && '(Calibrate both 2D and 3D pins side-by-side)'}
                            </span>
                          </div>
                          <div className="flex items-center bg-slate-200 dark:bg-slate-700 p-0.5 rounded-lg text-xs gap-0.5">
                            <button
                              type="button"
                              id="studio-placer-mode-2d-btn"
                              onClick={() => setStudioPlacerMode('2d')}
                              className={`px-3 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                studioPlacerMode === '2d'
                                  ? 'bg-blue-600 text-white shadow-2xs'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                              }`}
                            >
                              <MapPin className="w-3.5 h-3.5" />
                              <span>2D Flat Canvas</span>
                            </button>
                            <button
                              type="button"
                              id="studio-placer-mode-3d-btn"
                              onClick={() => setStudioPlacerMode('3d')}
                              className={`px-3 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                studioPlacerMode === '3d'
                                  ? 'bg-purple-600 text-white shadow-2xs'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                              }`}
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>360° 3D VR Photosphere</span>
                            </button>
                            <button
                              type="button"
                              id="studio-placer-mode-split-btn"
                              onClick={() => setStudioPlacerMode('split')}
                              className={`px-3 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                studioPlacerMode === 'split'
                                  ? 'bg-emerald-600 text-white shadow-2xs'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                              }`}
                              title="Calibrate 2D Canvas and 3D Photosphere Placers simultaneously"
                            >
                              <Columns className="w-3.5 h-3.5" />
                              <span>Split View (2D + 3D)</span>
                            </button>
                          </div>
                        </div>

                        {/* Inline Stage Image & Wix CDN Control Bar */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400">
                              Stage Background:
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              (selectedScenario.customImageUrl || selectedScenario.imageUrl)?.includes('wixstatic.com')
                                ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-300 dark:border-emerald-700'
                                : (selectedScenario.customImageUrl || selectedScenario.imageUrl)
                                ? 'bg-purple-500/10 text-purple-600 border border-purple-300 dark:border-purple-700'
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                            }`}>
                              {(selectedScenario.customImageUrl || selectedScenario.imageUrl)?.includes('wixstatic.com')
                                ? '✓ Hosted on Wix CDN'
                                : (selectedScenario.customImageUrl || selectedScenario.imageUrl)
                                ? 'Custom / AI Hosted'
                                : CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room']?.name || 'Built-in Room'}
                            </span>

                            {(selectedScenario.environment === 'handwashing_station' || selectedScenario.title.toLowerCase().includes('handwash')) && (
                              <button
                                type="button"
                                onClick={handleApplyHandwashingAlignment}
                                className="px-2 py-0.5 rounded-md bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/50 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 text-[10px] font-bold border border-blue-200 dark:border-blue-800 transition-colors flex items-center gap-1 cursor-pointer"
                                title="Auto-align all station pins to the Hand Hygiene sink, faucet, soap, towels, and bin"
                              >
                                <Sparkles className="w-3 h-3" />
                                <span>Auto-Align Handwashing Pins</span>
                              </button>
                            )}
                          </div>

                          <div className="flex items-center gap-1.5 flex-1 max-w-md">
                            <input
                              type="url"
                              value={wixUrlInput}
                              onChange={(e) => setWixUrlInput(e.target.value)}
                              placeholder="Paste Wix CDN Image URL (https://static.wixstatic.com/media/...)"
                              className="flex-1 p-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                            />
                            <button
                              type="button"
                              onClick={handleApplyWixUrl}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                            >
                              Apply URL
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUploadGeneratedImageToWix()}
                              disabled={isUploadingToWix}
                              className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs flex items-center gap-1 disabled:opacity-50"
                              title="Upload active stage image directly to Wix Media Manager (deletes base64 references automatically)"
                            >
                              {isUploadingToWix ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloudUpload className="w-3 h-3" />}
                              <span>{isUploadingToWix ? 'Uploading...' : 'Wix Upload'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleOpenAiImageModal}
                              className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs flex items-center gap-1"
                              title="Synthesize skill-aligned photorealistic photography with AI"
                            >
                              <Sparkles className="w-3 h-3" />
                              <span>AI Image</span>
                            </button>
                          </div>
                        </div>

                        {/* Placer Container: Supports 2D, 3D, or Split Side-by-Side Dual Placers */}
                        <div className={studioPlacerMode === 'split' ? "grid grid-cols-1 xl:grid-cols-2 gap-4" : "flex flex-col gap-2"}>
                          {/* 2D Flat Canvas Pin Placer (Rendered in '2d' or 'split' modes) */}
                          {(studioPlacerMode === '2d' || studioPlacerMode === 'split') && (
                            <div className="flex flex-col gap-2 p-2 rounded-xl bg-slate-50/50 dark:bg-slate-800/40 border border-blue-200/60 dark:border-blue-900/40">
                              <div className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  <span className="font-bold text-xs text-blue-900 dark:text-blue-200">
                                    2D Flat Room Canvas Pin Placer
                                  </span>
                                </div>
                                <span className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">
                                  Click room image to attach pin
                                </span>
                              </div>

                              <div 
                                className="relative w-full aspect-16/9 rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700 bg-slate-950 cursor-crosshair group select-none shadow-inner"
                                onClick={(e) => {
                                  if (selectedScenario.hotspots.length === 0) return;
                                  const targetIdx = (selectedHotspotIndex >= 0 && selectedHotspotIndex < selectedScenario.hotspots.length)
                                    ? selectedHotspotIndex
                                    : 0;
                                  if (targetIdx !== selectedHotspotIndex) {
                                    setSelectedHotspotIndex(targetIdx);
                                  }

                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const xPercent = Math.round(Math.max(2, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100)));
                                  const yPercent = Math.round(Math.max(2, Math.min(98, ((e.clientY - rect.top) / rect.height) * 100)));

                                  setStudio2DClickRipple({ x: xPercent, y: yPercent });
                                  setTimeout(() => setStudio2DClickRipple(null), 850);

                                  const targetStation = selectedScenario.hotspots[targetIdx];
                                  const updated = selectedScenario.hotspots.map((hs, i) => {
                                    if (i !== targetIdx) return hs;
                                    return {
                                      ...hs,
                                      coordinates: { x: xPercent, y: yPercent }
                                      // Keeps sphericalCoordinates 100% independent!
                                    };
                                  });
                                  setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                  flashStatus('success', `✓ Attached 2D pin for "${targetStation?.name || 'station'}" at X: ${xPercent}%, Y: ${yPercent}%`);
                                }}
                              >
                                <img 
                                  src={
                                    selectedScenario.customImageUrl || 
                                    selectedScenario.imageUrl || 
                                    CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room']?.imageUrl || 
                                    CLINICAL_ENVIRONMENTS.bedside_room.imageUrl
                                  }
                                  alt="Clinical Environment"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => {
                                    const target = e.currentTarget as HTMLImageElement;
                                    if (target.src !== window.location.origin + '/images/environments/bedside_room.jpg') {
                                      target.src = '/images/environments/bedside_room.jpg';
                                    }
                                  }}
                                  className="w-full h-full object-cover pointer-events-none"
                                />
                                <div className="absolute inset-0 bg-slate-950/15 group-hover:bg-slate-950/5 transition-colors pointer-events-none" />

                                {/* Interactive Click Ripple Indicator */}
                                {studio2DClickRipple && (
                                  <div 
                                    style={{ left: `${studio2DClickRipple.x}%`, top: `${studio2DClickRipple.y}%` }}
                                    className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30 flex items-center justify-center"
                                  >
                                    <div className="w-12 h-12 rounded-full border-2 border-cyan-400 bg-cyan-400/20 animate-ping" />
                                    <div className="absolute w-5 h-5 rounded-full border border-white bg-blue-500/50" />
                                    <Crosshair className="absolute w-3.5 h-3.5 text-white animate-pulse" />
                                  </div>
                                )}

                                {/* Hotspot 2D Pins */}
                                {selectedScenario.hotspots.map((hs, idx) => {
                                  const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                                  const coords = hs.coordinates || envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                  const isSelected = selectedHotspotIndex === idx;

                                  return (
                                    <div
                                      key={hs.id}
                                      style={{ left: `${coords.x}%`, top: `${coords.y}%` }}
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setSelectedHotspotIndex(idx);
                                        flashStatus('info', `Attached "${hs.name}" to 2D pin placer.`);
                                      }}
                                      className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform hover:scale-115 z-10 flex flex-col items-center ${
                                        isSelected ? 'scale-120 z-25' : 'opacity-90'
                                      }`}
                                      title={`${hs.name} (X: ${coords.x}%, Y: ${coords.y}%) - Click to select/attach station`}
                                    >
                                      {/* Target Radar Pulse when Active */}
                                      {isSelected && (
                                        <div className="absolute -inset-2 rounded-full border-2 border-cyan-400 animate-ping opacity-60 pointer-events-none" />
                                      )}

                                      <div className={`p-1.5 rounded-full shadow-lg border-2 flex items-center justify-center transition-all ${
                                        isSelected
                                          ? 'bg-blue-600 border-white text-white ring-4 ring-cyan-400/70 shadow-cyan-500/50'
                                          : 'bg-white/95 dark:bg-slate-800/95 border-blue-600 text-blue-700 dark:text-blue-300'
                                      }`}>
                                        {isSelected ? <Crosshair className="w-3.5 h-3.5 animate-pulse" /> : <MapPin className="w-3.5 h-3.5" />}
                                      </div>

                                      <span className={`mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap shadow-xs backdrop-blur-xs transition-colors flex items-center gap-1 ${
                                        isSelected
                                          ? 'bg-blue-600 text-white ring-2 ring-white/70 shadow-md'
                                          : 'bg-slate-900/85 text-white border border-white/20'
                                      }`}>
                                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-ping" />}
                                        <span>{hs.name}</span>
                                      </span>
                                    </div>
                                  );
                                })}

                                {/* Placement Crosshair Hint in Corner */}
                                <div className="absolute bottom-2 right-2 px-2.5 py-1 rounded-lg bg-slate-900/85 backdrop-blur-xs text-[10px] text-white flex items-center gap-1.5 pointer-events-none border border-white/10 shadow-sm">
                                  <Crosshair className="w-3.5 h-3.5 text-cyan-400" />
                                  <span>Click anywhere to attach active 2D pin</span>
                                </div>
                              </div>

                              {/* 2D Pin Coordinates, Direct Inputs & Precision Nudge Strip */}
                              {selectedScenario.hotspots[selectedHotspotIndex] && (() => {
                                const hs = selectedScenario.hotspots[selectedHotspotIndex];
                                const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                                const coords = hs.coordinates || envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                return (
                                  <div className="p-2 rounded-lg bg-slate-900 text-white flex items-center justify-between gap-2.5 text-xs border border-blue-500/30 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <MapPin className="w-4 h-4 text-blue-400 shrink-0" />
                                      <span className="font-bold text-slate-300 truncate max-w-[130px]">{hs.name}</span>
                                      
                                      {/* Direct Numeric Coordinate Inputs */}
                                      <div className="flex items-center gap-1">
                                        <span className="font-mono text-slate-400 text-[11px]">X:</span>
                                        <input
                                          type="number"
                                          min="2"
                                          max="98"
                                          value={coords.x}
                                          onChange={(e) => {
                                            const val = Math.max(2, Math.min(98, Number(e.target.value) || 0));
                                            const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: val, y: coords.y } } : h);
                                            setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          }}
                                          className="w-12 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-white font-mono text-[11px] text-center"
                                        />
                                        <span className="text-slate-500 font-mono text-[10px]">%</span>
                                      </div>

                                      <div className="flex items-center gap-1">
                                        <span className="font-mono text-slate-400 text-[11px]">Y:</span>
                                        <input
                                          type="number"
                                          min="2"
                                          max="98"
                                          value={coords.y}
                                          onChange={(e) => {
                                            const val = Math.max(2, Math.min(98, Number(e.target.value) || 0));
                                            const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: coords.x, y: val } } : h);
                                            setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          }}
                                          className="w-12 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-white font-mono text-[11px] text-center"
                                        />
                                        <span className="text-slate-500 font-mono text-[10px]">%</span>
                                      </div>
                                    </div>

                                    {/* Precision Nudge Buttons & Reset */}
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newX = Math.max(2, Math.min(98, coords.x - 1));
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: newX, y: coords.y } } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Left 1%"
                                      >
                                        ← X
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newX = Math.max(2, Math.min(98, coords.x + 1));
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: newX, y: coords.y } } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Right 1%"
                                      >
                                        X →
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newY = Math.max(2, Math.min(98, coords.y - 1));
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: coords.x, y: newY } } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Up 1%"
                                      >
                                        ↑ Y
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newY = Math.max(2, Math.min(98, coords.y + 1));
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: { x: coords.x, y: newY } } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Down 1%"
                                      >
                                        ↓ Y
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const defCoords = envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, coordinates: defCoords } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          flashStatus('info', `Reset 2D pin for "${hs.name}" to zone default.`);
                                        }}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 cursor-pointer text-[10px]"
                                        title="Reset 2D coordinates to zone default"
                                      >
                                        Reset 2D
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* 360° 3D VR Photosphere Pin Placer (Rendered in '3d' or 'split' modes) */}
                          {(studioPlacerMode === '3d' || studioPlacerMode === 'split') && (
                            <div className="flex flex-col gap-2 p-2 rounded-xl bg-slate-50/50 dark:bg-slate-800/40 border border-purple-200/60 dark:border-purple-900/40">
                              <div className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-1.5">
                                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  <span className="font-bold text-xs text-purple-900 dark:text-purple-200">
                                    360° 3D VR Photosphere Pin Placer
                                  </span>
                                </div>
                                <span className="text-[11px] text-purple-600 dark:text-purple-400 font-medium">
                                  Drag to look • Click in 3D to calibrate pin
                                </span>
                              </div>

                              <ClinicalPhotosphereStage
                                panoramaUrl={selectedScenario.photosphereUrl || CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room']?.photosphereUrl || '/images/environments/handwashing_360.jpg'}
                                fallbackImageUrl={selectedScenario.customImageUrl || selectedScenario.imageUrl || '/images/environments/bedside_room.jpg'}
                                hotspots={selectedScenario.hotspots}
                                activeHotspotId={selectedScenario.hotspots[selectedHotspotIndex]?.id || null}
                                onSelectHotspot={(hs) => {
                                  const idx = selectedScenario.hotspots.findIndex(h => h.id === hs.id);
                                  if (idx !== -1) setSelectedHotspotIndex(idx);
                                }}
                                isCalibrating={true}
                                calibratingHotspotId={selectedScenario.hotspots[selectedHotspotIndex]?.id || null}
                                onCalibrateHotspot={(hotspotId, spherical) => {
                                  const updated = selectedScenario.hotspots.map(h => {
                                    if (h.id !== hotspotId) return h;
                                    return {
                                      ...h,
                                      sphericalCoordinates: spherical
                                      // Keeps 2D flat coordinates 100% independent!
                                    };
                                  });
                                  setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                  flashStatus('success', `✓ Calibrated 3D coordinates for ${selectedScenario.hotspots[selectedHotspotIndex]?.name || 'station'}: Yaw ${spherical.yaw}°, Pitch ${spherical.pitch}°`);
                                }}
                                onNudgePin={(dYaw, dPitch) => {
                                  const activeHs = selectedScenario.hotspots[selectedHotspotIndex];
                                  if (!activeHs) return;
                                  const currentSpherical = activeHs.sphericalCoordinates || coords2DtoSpherical(
                                    activeHs.coordinates?.x ?? 50,
                                    activeHs.coordinates?.y ?? 50
                                  );
                                  const newSpherical = {
                                    yaw: Math.max(-180, Math.min(180, currentSpherical.yaw + dYaw)),
                                    pitch: Math.max(-85, Math.min(85, currentSpherical.pitch + dPitch))
                                  };
                                  const updated = selectedScenario.hotspots.map((h, i) => {
                                    if (i !== selectedHotspotIndex) return h;
                                    return {
                                      ...h,
                                      sphericalCoordinates: newSpherical
                                      // Keeps 2D flat coordinates 100% independent!
                                    };
                                  });
                                  setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                }}
                                environmentName={CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room']?.name || 'Clinical Room'}
                                completedActionIds={[]}
                              />

                              {/* 3D Pin Coordinates, Direct Inputs & Precision Nudge Strip */}
                              {selectedScenario.hotspots[selectedHotspotIndex] && (() => {
                                const hs = selectedScenario.hotspots[selectedHotspotIndex];
                                const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                                const spherical = hs.sphericalCoordinates || envDef.defaultSphericalCoordinates?.[hs.zone] || coords2DtoSpherical(hs.coordinates?.x ?? 50, hs.coordinates?.y ?? 50);
                                return (
                                  <div className="p-2 rounded-lg bg-slate-900 text-white flex items-center justify-between gap-2.5 text-xs border border-purple-500/30 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
                                      <span className="font-bold text-slate-300 truncate max-w-[130px]">{hs.name}</span>

                                      {/* Direct Numeric Yaw & Pitch Inputs */}
                                      <div className="flex items-center gap-1">
                                        <span className="font-mono text-slate-400 text-[11px]">Yaw:</span>
                                        <input
                                          type="number"
                                          min="-180"
                                          max="180"
                                          value={spherical.yaw}
                                          onChange={(e) => {
                                            const val = Math.max(-180, Math.min(180, Number(e.target.value) || 0));
                                            const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: { yaw: val, pitch: spherical.pitch } } : h);
                                            setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          }}
                                          className="w-14 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-white font-mono text-[11px] text-center"
                                        />
                                        <span className="text-slate-500 font-mono text-[10px]">°</span>
                                      </div>

                                      <div className="flex items-center gap-1">
                                        <span className="font-mono text-slate-400 text-[11px]">Pitch:</span>
                                        <input
                                          type="number"
                                          min="-85"
                                          max="85"
                                          value={spherical.pitch}
                                          onChange={(e) => {
                                            const val = Math.max(-85, Math.min(85, Number(e.target.value) || 0));
                                            const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: { yaw: spherical.yaw, pitch: val } } : h);
                                            setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          }}
                                          className="w-14 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-white font-mono text-[11px] text-center"
                                        />
                                        <span className="text-slate-500 font-mono text-[10px]">°</span>
                                      </div>
                                    </div>

                                    {/* Precision Nudge Buttons & Reset */}
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSpherical = { ...spherical, yaw: Math.max(-180, spherical.yaw - 2) };
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: newSpherical } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Left 2°"
                                      >
                                        ← Yaw
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSpherical = { ...spherical, yaw: Math.min(180, spherical.yaw + 2) };
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: newSpherical } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Right 2°"
                                      >
                                        Yaw →
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSpherical = { ...spherical, pitch: Math.min(85, spherical.pitch + 2) };
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: newSpherical } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Up 2°"
                                      >
                                        ↑ Pitch
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSpherical = { ...spherical, pitch: Math.max(-85, spherical.pitch - 2) };
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: newSpherical } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 cursor-pointer text-[11px]"
                                        title="Nudge Down 2°"
                                      >
                                        ↓ Pitch
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const defCoords = envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                          const defSpherical = envDef.defaultSphericalCoordinates?.[hs.zone] || coords2DtoSpherical(defCoords.x, defCoords.y);
                                          const updated = selectedScenario.hotspots.map((h, i) => i === selectedHotspotIndex ? { ...h, sphericalCoordinates: defSpherical } : h);
                                          setSelectedScenario({ ...selectedScenario, hotspots: updated });
                                          flashStatus('info', `Reset 3D spherical pin for "${hs.name}" to zone default.`);
                                        }}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 cursor-pointer text-[10px]"
                                        title="Reset 3D coordinates to zone default"
                                      >
                                        Reset 3D
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ATTACHED STEPS & ACTIONS MANAGER DIRECTLY UNDER THE PIN PLACER */}
                      {selectedScenario.hotspots[selectedHotspotIndex] && (() => {
                        const activeStation = selectedScenario.hotspots[selectedHotspotIndex];
                        const actions = activeStation.availableActions || [];

                        return (
                          <div className="p-4 rounded-2xl border-2 border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-slate-900/90 shadow-sm flex flex-col gap-3.5">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2.5 border-b border-blue-200/70 dark:border-slate-800">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-xs shadow-xs shrink-0">
                                  #{selectedHotspotIndex + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-black text-sm text-slate-900 dark:text-slate-100">
                                      Attached Steps for Object: <span className="text-blue-700 dark:text-blue-400 font-extrabold">{activeStation.name}</span>
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      actions.length > 0 ? 'bg-blue-600 text-white shadow-2xs' : 'bg-rose-500 text-white animate-pulse'
                                    }`}>
                                      {actions.length} {actions.length === 1 ? 'Step' : 'Steps'} Attached
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                    When a candidate targets this pin in the {studioPlacerMode === '3d' ? '360° Photosphere' : 'room stage'}, these clinical action steps are presented to execute.
                                  </p>
                                </div>
                              </div>

                              {/* Action Buttons for Station Steps */}
                              <div className="flex items-center gap-2 flex-wrap self-start sm:self-auto">
                                <button
                                  type="button"
                                  onClick={() => handleGenerateStationActionsWithAi(selectedHotspotIndex)}
                                  disabled={isGeneratingStationAi}
                                  className="px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 font-bold text-xs flex items-center gap-1.5 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-all cursor-pointer shadow-2xs active:scale-95"
                                  title="Synthesize numbered procedural steps for this station using AI"
                                >
                                  <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                                  <span>{isGeneratingStationAi ? 'Synthesizing...' : 'Generate Steps with AI'}</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={handleAutoNumberAllSteps}
                                  className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 font-bold text-xs flex items-center gap-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer shadow-2xs active:scale-95"
                                  title="Sequentially re-number all steps across all stations (Step 1, Step 2, Step 3...)"
                                >
                                  <ListOrdered className="w-3.5 h-3.5 text-blue-500" />
                                  <span>Auto-Number Steps</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => addActionToStation(selectedHotspotIndex)}
                                  className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs active:scale-95 transition-all cursor-pointer"
                                >
                                  <Plus className="w-4 h-4" />
                                  <span>+ Add Step</span>
                                </button>
                              </div>
                            </div>

                            {/* Steps List */}
                            {actions.length === 0 ? (
                              <div className="p-4 rounded-xl border-2 border-dashed border-amber-300 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-950/20 text-center flex flex-col items-center gap-2">
                                <AlertTriangle className="w-6 h-6 text-amber-500" />
                                <p className="text-xs font-bold text-amber-900 dark:text-amber-200">
                                  No steps currently attached to {activeStation.name}!
                                </p>
                                <p className="text-[11px] text-amber-700 dark:text-amber-400 max-w-md">
                                  In the 360° simulation, candidates will be able to target this pin placer, but will see no procedural steps or clinical actions to perform.
                                </p>
                                <div className="flex items-center gap-2 flex-wrap justify-center mt-1">
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateStationActionsWithAi(selectedHotspotIndex)}
                                    disabled={isGeneratingStationAi}
                                    className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-xs active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
                                  >
                                    <Sparkles className="w-4 h-4" />
                                    <span>{isGeneratingStationAi ? 'Synthesizing...' : `Generate Steps with AI for ${activeStation.name}`}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => addActionToStation(selectedHotspotIndex)}
                                    className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-xs active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
                                  >
                                    <Plus className="w-4 h-4" />
                                    <span>Attach First Step Manually</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2.5">
                                {actions.map((act, actIdx) => (
                                  <div
                                    key={act.actionId || actIdx}
                                    className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/90 shadow-2xs flex flex-col gap-2.5"
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex items-center gap-2 flex-1">
                                        <div className="flex items-center gap-1 shrink-0">
                                          <span className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 font-bold text-xs flex items-center justify-center shrink-0">
                                            #{actIdx + 1}
                                          </span>
                                          <div className="flex flex-col gap-0.5">
                                            <button
                                              type="button"
                                              disabled={actIdx === 0}
                                              onClick={() => moveStationAction(selectedHotspotIndex, actIdx, 'up')}
                                              className="p-1 rounded bg-slate-100 hover:bg-blue-100 dark:bg-slate-700 dark:hover:bg-blue-900 text-slate-600 hover:text-blue-600 dark:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                              title="Move action up (re-order)"
                                            >
                                              <ArrowUp className="w-3 h-3" />
                                            </button>
                                            <button
                                              type="button"
                                              disabled={actIdx === actions.length - 1}
                                              onClick={() => moveStationAction(selectedHotspotIndex, actIdx, 'down')}
                                              className="p-1 rounded bg-slate-100 hover:bg-blue-100 dark:bg-slate-700 dark:hover:bg-blue-900 text-slate-600 hover:text-blue-600 dark:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                              title="Move action down (re-order)"
                                            >
                                              <ArrowDown className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                        <input
                                          type="text"
                                          value={act.label}
                                          placeholder="Enter step action description (e.g., Lather hands and friction wrists for 20 seconds)"
                                          onChange={(e) => updateStationAction(selectedHotspotIndex, actIdx, { label: e.target.value })}
                                          className="flex-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-900 dark:text-slate-100 bg-slate-50/50 dark:bg-slate-900/50 focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>

                                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                                        {selectedScenario.hotspots.length > 1 && (
                                          <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/80 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-600">
                                            <ArrowRightLeft className="w-3 h-3 text-blue-500 shrink-0" />
                                            <select
                                              value=""
                                              onChange={(e) => {
                                                const targetIdx = parseInt(e.target.value);
                                                if (!isNaN(targetIdx) && targetIdx !== selectedHotspotIndex) {
                                                  moveActionToStation(selectedHotspotIndex, actIdx, targetIdx);
                                                }
                                              }}
                                              className="text-[10px] font-bold text-slate-700 dark:text-slate-200 bg-transparent border-none focus:outline-hidden cursor-pointer max-w-[150px] truncate"
                                              title="Move this action to another Station Name / Room Fixture"
                                            >
                                              <option value="" disabled>Move to Station...</option>
                                              {selectedScenario.hotspots.map((targetHs, tIdx) => {
                                                if (tIdx === selectedHotspotIndex) return null;
                                                return (
                                                  <option key={targetHs.id || tIdx} value={tIdx}>
                                                    → {targetHs.name || `Station #${tIdx + 1}`}
                                                  </option>
                                                );
                                              })}
                                            </select>
                                          </div>
                                        )}

                                        <label className="flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-[10px] font-bold text-amber-800 dark:text-amber-300">
                                          <input
                                            type="checkbox"
                                            checked={!!act.isCritical}
                                            onChange={(e) => updateStationAction(selectedHotspotIndex, actIdx, { isCritical: e.target.checked })}
                                            className="rounded text-amber-600"
                                          />
                                          <span>Critical Step</span>
                                        </label>

                                        <button
                                          type="button"
                                          onClick={() => deleteStationAction(selectedHotspotIndex, actIdx)}
                                          className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                                          title="Delete attached step"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Action Details Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                                      <div>
                                        <span className="text-[10px] font-semibold text-slate-500 block mb-1">Rubric Domain:</span>
                                        <select
                                          value={act.category || 'technique'}
                                          onChange={(e) => updateStationAction(selectedHotspotIndex, actIdx, { category: e.target.value as any })}
                                          className="w-full p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-medium"
                                        >
                                          <option value="technique">Technique & Procedural Steps</option>
                                          <option value="safety">Patient Safety & Body Mechanics</option>
                                          <option value="hygiene">Infection Control & Hygiene</option>
                                          <option value="dignity">Resident Dignity & Rights</option>
                                          <option value="communication">Communication & Identity Check</option>
                                        </select>
                                      </div>

                                      <div>
                                        <span className="text-[10px] font-semibold text-slate-500 block mb-1">Rubric Score Points:</span>
                                        <input
                                          type="number"
                                          min={0}
                                          max={50}
                                          value={act.pointsAwarded || 0}
                                          onChange={(e) => updateStationAction(selectedHotspotIndex, actIdx, { pointsAwarded: parseInt(e.target.value) || 0 })}
                                          className="w-full p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-bold"
                                        />
                                      </div>

                                      <div>
                                        <span className="text-[10px] font-semibold text-slate-500 block mb-1">Examiner Feedback / Dialogue:</span>
                                        <input
                                          type="text"
                                          value={act.feedbackDialogue || ''}
                                          placeholder="Examiner verbal prompt when done..."
                                          onChange={(e) => updateStationAction(selectedHotspotIndex, actIdx, { feedbackDialogue: e.target.value })}
                                          className="w-full p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800 dark:text-slate-200">
                          Room Stations & Interactive Hotspots
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                            const def2D = envDef.defaultHotspotCoordinates['supplies'] || { x: 50, y: 50 };
                            const def3D = envDef.defaultSphericalCoordinates?.['supplies'] || coords2DtoSpherical(def2D.x, def2D.y);
                            const newStation: SimulatorHotspot = {
                              id: `hotspot_${Date.now()}`,
                              name: 'New Clinical Station',
                              zone: 'supplies',
                              coordinates: { x: def2D.x, y: def2D.y },
                              sphericalCoordinates: { yaw: def3D.yaw, pitch: def3D.pitch },
                              requiredPhase: 'any',
                              availableActions: [
                                {
                                  actionId: `act_${Date.now()}`,
                                  label: 'Inspect Supplies',
                                  feedbackDialogue: 'Candidate verifies supplies are intact.',
                                  isCritical: false,
                                  pointsAwarded: 10,
                                  category: 'technique'
                                }
                              ]
                            };
                            setSelectedScenario({
                              ...selectedScenario,
                              hotspots: [...selectedScenario.hotspots, newStation]
                            });
                            setSelectedHotspotIndex(selectedScenario.hotspots.length);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-bold border border-blue-200 dark:border-blue-800 hover:bg-blue-100"
                        >
                          + Add Station
                        </button>
                      </div>

                      <div className="flex flex-col gap-3">
                        {selectedScenario.hotspots.map((hs, hIdx) => {
                          const envDef = CLINICAL_ENVIRONMENTS[selectedScenario.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
                          const currentCoords = hs.coordinates || envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                          const isStationActive = selectedHotspotIndex === hIdx;

                          return (
                          <div 
                            key={hs.id} 
                            id={`station-card-${hs.id}`}
                            className={`p-4 rounded-xl border-2 transition-all flex flex-col gap-3.5 ${
                              isStationActive 
                                ? 'border-blue-600 bg-blue-50/20 dark:bg-blue-950/20 shadow-xs' 
                                : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
                            }`}
                          >
                            {/* Station Header & Controls */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex-1 flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Station Name / Room Fixture
                                  </label>
                                  {selectedScenario.hotspots.length > 1 && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-[10px] font-mono font-bold text-slate-400">
                                        Station #{hIdx + 1}
                                      </span>
                                      <button
                                        type="button"
                                        disabled={hIdx === 0}
                                        onClick={() => moveStation(hIdx, 'up')}
                                        className="p-1 rounded bg-slate-200 dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-slate-700 dark:text-slate-200 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                        title="Move Station / Room Fixture up"
                                      >
                                        <ArrowUp className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={hIdx === selectedScenario.hotspots.length - 1}
                                        onClick={() => moveStation(hIdx, 'down')}
                                        className="p-1 rounded bg-slate-200 dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-slate-700 dark:text-slate-200 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                        title="Move Station / Room Fixture down"
                                      >
                                        <ArrowDown className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <input
                                  id={`station-name-input-${hs.id}`}
                                  type="text"
                                  value={hs.name}
                                  onChange={(e) => updateStation(hIdx, { name: e.target.value })}
                                  placeholder="e.g. Bedside Table & Call Light"
                                  className="font-bold text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-hidden text-slate-900 dark:text-slate-100 w-full"
                                />
                              </div>

                              <div className="flex items-center gap-2 self-start sm:self-end">
                                <div className="flex flex-col gap-1">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Room Zone
                                  </label>
                                  <select
                                    id={`station-zone-select-${hs.id}`}
                                    value={hs.zone}
                                    onChange={(e) => {
                                      const newZone = e.target.value as any;
                                      const defCoords = envDef.defaultHotspotCoordinates[newZone] || { x: 50, y: 50 };
                                      updateStation(hIdx, {
                                        zone: newZone,
                                        coordinates: hs.coordinates || defCoords
                                      });
                                    }}
                                    className="p-1.5 rounded-lg text-xs font-semibold border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                                  >
                                    <option value="sink">Hand Hygiene Sink</option>
                                    <option value="bed">Resident Hospital Bed</option>
                                    <option value="resident">Resident / Patient</option>
                                    <option value="supplies">Medical Supplies Cart</option>
                                    <option value="privacy_curtain">Privacy Curtain</option>
                                    <option value="call_light">Call Light System</option>
                                    <option value="waste_hamper">Waste & Linen Hamper</option>
                                  </select>
                                </div>

                                <button
                                  type="button"
                                  id={`delete-station-btn-${hs.id}`}
                                  onClick={() => deleteStation(hIdx)}
                                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 p-2 rounded-lg transition-colors mt-4"
                                  title="Delete Station"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {/* Hotspot Independent 2D and 3D Pin Placement Section */}
                            <div className="flex flex-col gap-2 p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                              <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                                <span>Interactive Hotspot Pin Placer Properties (Independent 2D & 3D)</span>
                                <span className="text-[11px] font-normal text-slate-500">2D Canvas and 360° VR maintain distinct coordinates</span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                                {/* 2D Flat Canvas Pin Property */}
                                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-blue-200 dark:border-blue-900/50 flex flex-col gap-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 font-bold text-xs text-blue-700 dark:text-blue-300">
                                      <MapPin className="w-3.5 h-3.5" />
                                      <span>2D Flat Canvas Coordinates</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedHotspotIndex(hIdx);
                                        setStudioPlacerMode('2d');
                                        document.getElementById('studio-visual-pin-placer')?.scrollIntoView({ behavior: 'smooth' });
                                        flashStatus('info', `Attached "${hs.name}" to 2D Flat Canvas Pin Placer.`);
                                      }}
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                        isStationActive && (studioPlacerMode === '2d' || studioPlacerMode === 'split')
                                          ? 'bg-blue-600 text-white shadow-2xs'
                                          : 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:bg-blue-50'
                                      }`}
                                    >
                                      {isStationActive && (studioPlacerMode === '2d' || studioPlacerMode === 'split') ? '✓ Attached to 2D Placer' : 'Attach to 2D Placer'}
                                    </button>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs flex-wrap">
                                    <div className="flex items-center gap-1 flex-1 min-w-[90px]">
                                      <span className="font-mono text-slate-500 font-bold">X:</span>
                                      <input
                                        type="number"
                                        min="2"
                                        max="98"
                                        value={currentCoords.x}
                                        onChange={(e) => {
                                          const val = Math.max(2, Math.min(98, Number(e.target.value) || 0));
                                          updateStation(hIdx, { coordinates: { x: val, y: currentCoords.y } });
                                        }}
                                        className="w-full px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 font-mono text-xs text-center"
                                      />
                                      <span className="text-slate-400 font-mono">%</span>
                                    </div>

                                    <div className="flex items-center gap-1 flex-1 min-w-[90px]">
                                      <span className="font-mono text-slate-500 font-bold">Y:</span>
                                      <input
                                        type="number"
                                        min="2"
                                        max="98"
                                        value={currentCoords.y}
                                        onChange={(e) => {
                                          const val = Math.max(2, Math.min(98, Number(e.target.value) || 0));
                                          updateStation(hIdx, { coordinates: { x: currentCoords.x, y: val } });
                                        }}
                                        className="w-full px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 font-mono text-xs text-center"
                                      />
                                      <span className="text-slate-400 font-mono">%</span>
                                    </div>

                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newX = Math.max(2, currentCoords.x - 1);
                                          updateStation(hIdx, { coordinates: { x: newX, y: currentCoords.y } });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                        title="Nudge 2D Left 1%"
                                      >
                                        ←
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newX = Math.min(98, currentCoords.x + 1);
                                          updateStation(hIdx, { coordinates: { x: newX, y: currentCoords.y } });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                        title="Nudge 2D Right 1%"
                                      >
                                        →
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newY = Math.max(2, currentCoords.y - 1);
                                          updateStation(hIdx, { coordinates: { x: currentCoords.x, y: newY } });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                        title="Nudge 2D Up 1%"
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newY = Math.min(98, currentCoords.y + 1);
                                          updateStation(hIdx, { coordinates: { x: currentCoords.x, y: newY } });
                                        }}
                                        className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                        title="Nudge 2D Down 1%"
                                      >
                                        ↓
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const defCoords = envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                          updateStation(hIdx, { coordinates: defCoords });
                                          flashStatus('info', `Reset 2D coordinate for "${hs.name}" to zone default.`);
                                        }}
                                        className="text-[10px] text-slate-500 hover:text-blue-600 underline whitespace-nowrap cursor-pointer ml-1"
                                        title="Reset 2D coordinate to zone default"
                                      >
                                        Reset 2D
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* 3D Photosphere Pin Property */}
                                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-purple-200 dark:border-purple-900/50 flex flex-col gap-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 font-bold text-xs text-purple-700 dark:text-purple-300">
                                      <Sparkles className="w-3.5 h-3.5" />
                                      <span>360° 3D VR Spherical Coordinates</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedHotspotIndex(hIdx);
                                        setStudioPlacerMode('3d');
                                        document.getElementById('studio-visual-pin-placer')?.scrollIntoView({ behavior: 'smooth' });
                                        flashStatus('info', `Attached "${hs.name}" to 360° 3D VR Photosphere Placer.`);
                                      }}
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                        isStationActive && (studioPlacerMode === '3d' || studioPlacerMode === 'split')
                                          ? 'bg-purple-600 text-white shadow-2xs'
                                          : 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-700 hover:bg-purple-50'
                                      }`}
                                    >
                                      {isStationActive && (studioPlacerMode === '3d' || studioPlacerMode === 'split') ? '✓ Attached to 3D Placer' : 'Attach to 3D Placer'}
                                    </button>
                                  </div>

                                  {(() => {
                                    const spherical = hs.sphericalCoordinates || coords2DtoSpherical(currentCoords.x, currentCoords.y);
                                    return (
                                      <div className="flex items-center gap-2 text-xs flex-wrap">
                                        <div className="flex items-center gap-1 flex-1 min-w-[90px]">
                                          <span className="font-mono text-slate-500 font-bold">Yaw:</span>
                                          <input
                                            type="number"
                                            min="-180"
                                            max="180"
                                            value={spherical.yaw}
                                            onChange={(e) => {
                                              const val = Math.max(-180, Math.min(180, Number(e.target.value) || 0));
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: val, pitch: spherical.pitch } });
                                            }}
                                            className="w-full px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 font-mono text-xs text-center"
                                          />
                                          <span className="text-slate-400 font-mono">°</span>
                                        </div>

                                        <div className="flex items-center gap-1 flex-1 min-w-[90px]">
                                          <span className="font-mono text-slate-500 font-bold">Pitch:</span>
                                          <input
                                            type="number"
                                            min="-85"
                                            max="85"
                                            value={spherical.pitch}
                                            onChange={(e) => {
                                              const val = Math.max(-85, Math.min(85, Number(e.target.value) || 0));
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: spherical.yaw, pitch: val } });
                                            }}
                                            className="w-full px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 font-mono text-xs text-center"
                                          />
                                          <span className="text-slate-400 font-mono">°</span>
                                        </div>

                                        <div className="flex items-center gap-1">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newYaw = Math.max(-180, spherical.yaw - 2);
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: newYaw, pitch: spherical.pitch } });
                                            }}
                                            className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                            title="Nudge 3D Yaw -2°"
                                          >
                                            ←
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newYaw = Math.min(180, spherical.yaw + 2);
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: newYaw, pitch: spherical.pitch } });
                                            }}
                                            className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                            title="Nudge 3D Yaw +2°"
                                          >
                                            →
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newPitch = Math.min(85, spherical.pitch + 2);
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: spherical.yaw, pitch: newPitch } });
                                            }}
                                            className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                            title="Nudge 3D Pitch +2°"
                                          >
                                            ↑
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newPitch = Math.max(-85, spherical.pitch - 2);
                                              updateStation(hIdx, { sphericalCoordinates: { yaw: spherical.yaw, pitch: newPitch } });
                                            }}
                                            className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-mono cursor-pointer"
                                            title="Nudge 3D Pitch -2°"
                                          >
                                            ↓
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const defCoords = envDef.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                                              const defSpherical = envDef.defaultSphericalCoordinates?.[hs.zone] || coords2DtoSpherical(defCoords.x, defCoords.y);
                                              updateStation(hIdx, { sphericalCoordinates: defSpherical });
                                              flashStatus('info', `Reset 3D spherical coordinate for "${hs.name}" to zone default.`);
                                            }}
                                            className="text-[10px] text-slate-500 hover:text-purple-600 underline whitespace-nowrap cursor-pointer ml-1"
                                            title="Reset 3D coordinate to zone default"
                                          >
                                            Reset 3D
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>

                            {/* Actions list within hotspot */}
                            <div className="flex flex-col gap-2.5 pl-3 border-l-2 border-blue-300 dark:border-blue-700/60 mt-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                  Station Clinical Actions ({hs.availableActions.length})
                                </span>
                                <span className="text-[11px] text-slate-500">
                                  Actions candidate can execute when clicking this hotspot
                                </span>
                              </div>

                              {hs.availableActions.map((act, aIdx) => (
                                <div 
                                  key={act.actionId} 
                                  id={`action-item-${act.actionId}`}
                                  className="p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex flex-col gap-2 shadow-2xs"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex-1 flex flex-col gap-1">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                          <span className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 font-bold text-[10px] flex items-center justify-center shrink-0">
                                            #{aIdx + 1}
                                          </span>
                                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                            Step Action Label
                                          </label>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            type="button"
                                            disabled={aIdx === 0}
                                            onClick={() => moveStationAction(hIdx, aIdx, 'up')}
                                            className="p-1 rounded bg-slate-100 hover:bg-blue-100 dark:bg-slate-800 dark:hover:bg-blue-900 text-slate-600 hover:text-blue-600 dark:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                            title="Move action up (re-order)"
                                          >
                                            <ArrowUp className="w-3 h-3" />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={aIdx === hs.availableActions.length - 1}
                                            onClick={() => moveStationAction(hIdx, aIdx, 'down')}
                                            className="p-1 rounded bg-slate-100 hover:bg-blue-100 dark:bg-slate-800 dark:hover:bg-blue-900 text-slate-600 hover:text-blue-600 dark:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                                            title="Move action down (re-order)"
                                          >
                                            <ArrowDown className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                      <input
                                        id={`action-label-input-${act.actionId}`}
                                        type="text"
                                        value={act.label}
                                        onChange={(e) => updateStationAction(hIdx, aIdx, { label: e.target.value })}
                                        className="font-semibold text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:outline-hidden w-full"
                                        placeholder="e.g. Wash hands with soap and water for 20 seconds..."
                                      />
                                    </div>

                                    <div className="flex items-center gap-2 self-start sm:self-end flex-wrap">
                                      {selectedScenario.hotspots.length > 1 && (
                                        <div className="flex flex-col gap-1">
                                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                            Move to Station
                                          </label>
                                          <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600">
                                            <ArrowRightLeft className="w-3 h-3 text-blue-500 shrink-0" />
                                            <select
                                              value=""
                                              onChange={(e) => {
                                                const targetIdx = parseInt(e.target.value);
                                                if (!isNaN(targetIdx) && targetIdx !== hIdx) {
                                                  moveActionToStation(hIdx, aIdx, targetIdx);
                                                }
                                              }}
                                              className="text-[10px] font-bold text-slate-700 dark:text-slate-300 bg-transparent border-none focus:outline-hidden cursor-pointer max-w-[130px] truncate"
                                              title="Move this action to another Station Name / Room Fixture"
                                            >
                                              <option value="" disabled>Move to...</option>
                                              {selectedScenario.hotspots.map((targetHs, tIdx) => {
                                                if (tIdx === hIdx) return null;
                                                return (
                                                  <option key={targetHs.id || tIdx} value={tIdx}>
                                                    → {targetHs.name || `Station #${tIdx + 1}`}
                                                  </option>
                                                );
                                              })}
                                            </select>
                                          </div>
                                        </div>
                                      )}

                                      <div className="flex flex-col gap-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                          Category
                                        </label>
                                        <select
                                          value={act.category || 'technique'}
                                          onChange={(e) => updateStationAction(hIdx, aIdx, { category: e.target.value as any })}
                                          className="p-1 rounded-md text-[11px] font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                        >
                                          <option value="technique">Technique</option>
                                          <option value="safety">Safety</option>
                                          <option value="hygiene">Hygiene</option>
                                          <option value="dignity">Dignity</option>
                                        </select>
                                      </div>

                                      <div className="flex flex-col gap-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                          Points
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          value={act.pointsAwarded}
                                          onChange={(e) => updateStationAction(hIdx, aIdx, { pointsAwarded: parseInt(e.target.value) || 0 })}
                                          className="w-14 p-1 text-center text-xs font-bold border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                                        />
                                      </div>

                                      <label className="flex items-center gap-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2 py-1 rounded-md border border-amber-200 dark:border-amber-800 cursor-pointer mt-4">
                                        <input
                                          type="checkbox"
                                          checked={act.isCritical}
                                          onChange={(e) => updateStationAction(hIdx, aIdx, { isCritical: e.target.checked })}
                                          className="rounded text-amber-600 focus:ring-amber-500"
                                        />
                                        Critical
                                      </label>

                                      <button
                                        type="button"
                                        onClick={() => deleteStationAction(hIdx, aIdx)}
                                        className="text-slate-400 hover:text-rose-500 p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors mt-4"
                                        title="Delete action"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                      Examiner Feedback / Patient Dialogue
                                    </label>
                                    <input
                                      id={`action-dialogue-input-${act.actionId}`}
                                      type="text"
                                      value={act.feedbackDialogue}
                                      onChange={(e) => updateStationAction(hIdx, aIdx, { feedbackDialogue: e.target.value })}
                                      className="text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-hidden w-full"
                                      placeholder="Feedback displayed when candidate performs this action..."
                                    />
                                  </div>
                                </div>
                              ))}

                              <button
                                type="button"
                                id={`add-action-btn-${hs.id}`}
                                onClick={() => addActionToStation(hIdx)}
                                className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-bold text-xs self-start mt-1 hover:bg-blue-100 dark:hover:bg-blue-900/60 border border-blue-200 dark:border-blue-800 transition-colors flex items-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Add Action to this Station</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  )}

                  {/* Sub-Tab 4: State Board Instant Fail Traps */}
                  {editorSubTab === 'traps' && (
                    <div className="flex flex-col gap-4 text-xs">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-800 dark:text-slate-200 block">
                            Automatic State Board Disqualification Traps
                          </span>
                          <span className="text-[11px] text-slate-500">
                            Immediate exam invalidation triggers based on Credentia / Prometric criteria.
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newTrap: SimulatorInstantTrap = {
                              id: `trap_${Date.now()}`,
                              name: 'New Critical Failure Trap',
                              triggerCondition: 'condition_key',
                              boardRationale: 'State Board Mandatory Fail: Explanation of safety violation.'
                            };
                            setSelectedScenario({
                              ...selectedScenario,
                              instantFailTraps: [...selectedScenario.instantFailTraps, newTrap]
                            });
                          }}
                          className="px-2.5 py-1 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-bold border border-rose-200 dark:border-rose-800 hover:bg-rose-100"
                        >
                          + Add Trap
                        </button>
                      </div>

                      <div className="flex flex-col gap-3">
                        {selectedScenario.instantFailTraps.map((trap, tIdx) => (
                          <div key={trap.id} className="p-4 rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 flex flex-col gap-2.5">
                            <div className="flex items-center justify-between">
                              <input
                                type="text"
                                value={trap.name}
                                onChange={(e) => {
                                  const updated = [...selectedScenario.instantFailTraps];
                                  updated[tIdx].name = e.target.value;
                                  setSelectedScenario({ ...selectedScenario, instantFailTraps: updated });
                                }}
                                className="font-bold text-sm bg-transparent border-b border-rose-300 dark:border-rose-800 focus:outline-hidden text-rose-900 dark:text-rose-200 flex-1 mr-3"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (selectedScenario.instantFailTraps || []).filter((_, i) => i !== tIdx);
                                  setSelectedScenario({ ...selectedScenario, instantFailTraps: updated });
                                }}
                                className="text-rose-500 hover:text-rose-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-slate-500 uppercase">Board Evaluator Standard Rationale</label>
                              <textarea
                                rows={2}
                                value={trap.boardRationale || ''}
                                onChange={(e) => {
                                  const updated = [...(selectedScenario.instantFailTraps || [])];
                                  if (updated[tIdx]) {
                                    updated[tIdx] = { ...updated[tIdx], boardRationale: e.target.value };
                                    setSelectedScenario({ ...selectedScenario, instantFailTraps: updated });
                                  }
                                }}
                                className="w-full p-2 mt-0.5 rounded-lg border border-rose-200 dark:border-rose-900 bg-white dark:bg-slate-800 text-xs"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-Tab 5: Rubric Milestones Weights */}
                  {editorSubTab === 'rubric' && (
                    <div className="flex flex-col gap-4 text-xs">
                      <span className="font-bold text-slate-800 dark:text-slate-200">
                        Four-Pillar Score Weights (Total should equal 100 points)
                      </span>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                          <label className="block font-bold text-slate-800 dark:text-slate-200 mb-1">
                            Infection Control Weight (pts)
                          </label>
                          <input
                            type="number"
                            value={selectedScenario.rubricMilestones?.infectionControl?.totalWeight ?? 25}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              rubricMilestones: {
                                ...(selectedScenario.rubricMilestones || {}),
                                infectionControl: {
                                  requiredActions: [],
                                  ...(selectedScenario.rubricMilestones?.infectionControl || {}),
                                  totalWeight: parseInt(e.target.value) || 0
                                }
                              } as any
                            })}
                            className="w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                          />
                        </div>

                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                          <label className="block font-bold text-slate-800 dark:text-slate-200 mb-1">
                            Resident Dignity Weight (pts)
                          </label>
                          <input
                            type="number"
                            value={selectedScenario.rubricMilestones?.residentDignity?.totalWeight ?? 25}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              rubricMilestones: {
                                ...(selectedScenario.rubricMilestones || {}),
                                residentDignity: {
                                  requiredActions: [],
                                  ...(selectedScenario.rubricMilestones?.residentDignity || {}),
                                  totalWeight: parseInt(e.target.value) || 0
                                }
                              } as any
                            })}
                            className="w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                          />
                        </div>

                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                          <label className="block font-bold text-slate-800 dark:text-slate-200 mb-1">
                            Safety & Mechanics Weight (pts)
                          </label>
                          <input
                            type="number"
                            value={selectedScenario.rubricMilestones?.safetyMechanics?.totalWeight ?? 25}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              rubricMilestones: {
                                ...(selectedScenario.rubricMilestones || {}),
                                safetyMechanics: {
                                  requiredActions: [],
                                  ...(selectedScenario.rubricMilestones?.safetyMechanics || {}),
                                  totalWeight: parseInt(e.target.value) || 0
                                }
                              } as any
                            })}
                            className="w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                          />
                        </div>

                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                          <label className="block font-bold text-slate-800 dark:text-slate-200 mb-1">
                            Procedural Technique Weight (pts)
                          </label>
                          <input
                            type="number"
                            value={selectedScenario.rubricMilestones?.proceduralTechnique?.totalWeight ?? 25}
                            onChange={(e) => setSelectedScenario({
                              ...selectedScenario,
                              rubricMilestones: {
                                ...(selectedScenario.rubricMilestones || {}),
                                proceduralTechnique: {
                                  requiredActions: [],
                                  ...(selectedScenario.rubricMilestones?.proceduralTechnique || {}),
                                  totalWeight: parseInt(e.target.value) || 0
                                }
                              } as any
                            })}
                            className="w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={`${theme.cardBg} p-12 text-center text-slate-500 border border-slate-200 dark:border-slate-800 rounded-2xl`}>
              Select a scenario from the left or create a new one to begin authoring.
            </div>
          )}
        </div>
      </div>

      {/* Super Admin AI Photorealistic Image Synthesizer Modal */}
      {isAiImageModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-xl w-full p-6 flex flex-col gap-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 border border-purple-200 dark:border-purple-800">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 dark:text-slate-100">
                    Generate Photorealistic Clinical Imagery
                  </h3>
                  <p className="text-xs text-slate-500">
                    Powered by Imagen 3 on Vertex/Gemini AI for high-fidelity clinical simulation rooms
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAiImageModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-col gap-3 text-xs max-h-[75vh] overflow-y-auto pr-1">
              <div>
                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Clinical Environment Archetype
                </label>
                <select
                  value={aiEnvironment}
                  onChange={(e) => handleAiEnvironmentChange(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold"
                >
                  <option value="handwashing_station">Clinical Hand Hygiene & Sink Station (State Board Standard)</option>
                  <option value="bedside_room">Long-Term Care / Hospital Bedside Room</option>
                  <option value="exam_room">Outpatient Medical Examination Room</option>
                  <option value="phlebotomy_chair">Phlebotomy Drawing Chair Station</option>
                  <option value="icu_bay">Intensive Care & Monitoring Bay</option>
                </select>
              </div>

              {/* Format Mode: Standard 16:9 vs 360° VR Panorama */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-purple-50/70 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    Projection Format:
                  </span>
                </div>
                <div className="flex items-center gap-1 bg-white dark:bg-slate-900 p-1 rounded-lg border border-purple-200 dark:border-purple-800 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setIs360PanoramaMode(false);
                      const p = buildSmartClinicalPrompt(
                        aiEnvironment,
                        selectedScenario?.title,
                        selectedAiComponents,
                        false,
                        includeStudentActor
                      );
                      setAiPrompt(p);
                    }}
                    className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                      !is360PanoramaMode
                        ? 'bg-purple-600 text-white shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    16:9 Photography
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIs360PanoramaMode(true);
                      const p = buildSmartClinicalPrompt(
                        aiEnvironment,
                        selectedScenario?.title,
                        selectedAiComponents,
                        true,
                        includeStudentActor
                      );
                      setAiPrompt(p);
                    }}
                    className={`px-2.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 ${
                      is360PanoramaMode
                        ? 'bg-purple-600 text-white shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>360° VR Panorama</span>
                  </button>
                </div>
              </div>

              {/* Include Student / Actor in Scene Toggle */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                      Include Student / Healthcare Actor in 360° Scene
                    </span>
                    <span className="text-[10px] text-slate-500 block">
                      Adds an authentic candidate in royal blue scrubs demonstrating procedural technique for full clinical context.
                    </span>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-2">
                  <input
                    type="checkbox"
                    checked={includeStudentActor}
                    onChange={(e) => {
                      const nextVal = e.target.checked;
                      setIncludeStudentActor(nextVal);
                      const p = buildSmartClinicalPrompt(
                        aiEnvironment,
                        selectedScenario?.title,
                        selectedAiComponents,
                        is360PanoramaMode,
                        nextVal
                      );
                      setAiPrompt(p);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Instant Reference Preset for Hand Hygiene */}
              {aiEnvironment === 'handwashing_station' && (
                <div className="p-3 rounded-xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <img 
                      src="/images/environments/handwashing_station.jpg" 
                      alt="Handwashing Station"
                      className="w-14 h-10 object-cover rounded-md border border-blue-300 dark:border-blue-700 shrink-0" 
                    />
                    <div>
                      <span className="font-bold text-slate-900 dark:text-slate-100 block text-xs">
                        State Board Hand Hygiene Station Reference
                      </span>
                      <span className="text-[11px] text-slate-500 block">
                        Authentic scrub room with running water faucet, soap dispenser, towels, and bin.
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedAiImageUrl('/images/environments/handwashing_station.jpg');
                      flashStatus('success', 'Loaded official handwashing station reference scene');
                    }}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                  >
                    Use Reference Scene
                  </button>
                </div>
              )}

              {/* Customizable Clinical Fixtures / Components Checklist */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-bold text-slate-700 dark:text-slate-300">
                    Clinical Skill Components to Include in Photographic Scene
                  </label>
                  <span className="text-[11px] text-purple-600 dark:text-purple-400 font-semibold">
                    {selectedAiComponents.length} fixtures selected
                  </span>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mr-1">Presets:</span>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('nurse_patient')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900 cursor-pointer"
                  >
                    👩‍⚕️ Nurse + Patient Bedside
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('ambulation')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
                  >
                    🚶 Gait Belt & Ambulation
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('vitals')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900 cursor-pointer"
                  >
                    💓 Radial Pulse & Vitals
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('hygiene')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-teal-50 dark:bg-teal-950/60 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900 cursor-pointer"
                  >
                    🧼 Hand Hygiene Sink
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('phlebotomy')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900 cursor-pointer"
                  >
                    🩸 Phlebotomy Station
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('exam')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900 cursor-pointer"
                  >
                    🩺 Clinic Exam Table
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('patient_care')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-sky-50 dark:bg-sky-950/60 border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900 cursor-pointer"
                  >
                    🧽 Bathing & Hygiene
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('bed_making')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900 cursor-pointer"
                  >
                    🛏️ Bed Making & Linens
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('all')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('none')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Clear
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  {CLINICAL_SKILL_FIXTURES.map(fixture => {
                    const isChecked = selectedAiComponents.includes(fixture.id);
                    return (
                      <button
                        key={fixture.id}
                        type="button"
                        onClick={() => handleToggleAiComponent(fixture.id)}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-[11px] font-medium border transition-colors ${
                          isChecked
                            ? 'bg-purple-50 dark:bg-purple-950/60 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-3.5 h-3.5 rounded flex items-center justify-center border text-[9px] ${
                          isChecked 
                            ? 'bg-purple-600 border-purple-600 text-white' 
                            : 'border-slate-300 dark:border-slate-600'
                        }`}>
                          {isChecked && '✓'}
                        </div>
                        <span className="truncate">{fixture.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Synthesizer Visual Guidance Prompt (Auto-Updated from Components)
                </label>
                <textarea
                  rows={3}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g. A healthcare candidate in scrubs washing hands at a stainless steel clinical sink with running water faucet, wall soap dispenser, paper towel dispenser..."
                  className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 leading-relaxed font-normal"
                />
              </div>

              {generatedAiImageUrl && (
                <div className="flex flex-col gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[11px] text-slate-700 dark:text-slate-300">
                        Synthesized Photorealistic Result:
                      </span>
                      {generatedAiImageUrl.includes('static.wixstatic.com/media/') ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Hosted on Wix CDN
                        </span>
                      ) : generatedAiImageUrl.startsWith('data:') ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300">
                          Base64 Data (Ready for Wix upload)
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => handleUploadGeneratedImageToWix()}
                        disabled={isUploadingToWix || generatedAiImageUrl.includes('static.wixstatic.com/media/')}
                        className="px-2.5 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-2xs cursor-pointer disabled:opacity-50"
                        title="Upload image directly to Wix Media Manager and automatically delete any base64 payload"
                      >
                        {isUploadingToWix ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}
                        <span>{isUploadingToWix ? 'Uploading to Wix...' : generatedAiImageUrl.includes('static.wixstatic.com/media/') ? 'Uploaded to Wix' : 'Upload Directly to Wix Media'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = generatedAiImageUrl;
                          a.download = `${selectedScenario?.id || 'clinical-stage'}-${aiEnvironment}.jpg`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          flashStatus('success', 'Image downloaded. You can now upload it to your Wix Media Manager.');
                        }}
                        className="px-2.5 py-1 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-slate-600 font-bold text-xs flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer shadow-2xs"
                      >
                        <Download className="w-3.5 h-3.5 text-blue-600" />
                        <span>Download Image</span>
                      </button>
                    </div>
                  </div>
                  <div className="aspect-16/9 rounded-lg overflow-hidden bg-slate-950 border border-slate-300 dark:border-slate-700">
                    <img 
                      src={generatedAiImageUrl} 
                      alt="AI Generated" 
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover" 
                    />
                  </div>

                  {generatedAiImageUrl.startsWith('data:') && (
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-300 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span>Temporary Base64 preview image. Click <strong>Upload Directly to Wix Media</strong> to host permanently and clean up base64 payload.</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUploadGeneratedImageToWix()}
                        disabled={isUploadingToWix}
                        className="px-2 py-0.5 rounded bg-amber-600 text-white font-bold text-[10px] shrink-0 hover:bg-amber-700"
                      >
                        Upload to Wix Now
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 pt-1">
                    <input
                      type="url"
                      value={wixUrlInput}
                      onChange={(e) => setWixUrlInput(e.target.value)}
                      placeholder="Paste Wix CDN Image URL (https://static.wixstatic.com/media/...)"
                      className="flex-1 p-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={handleApplyWixUrl}
                      className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                    >
                      Save Wix Link
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setIsAiImageModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleGenerateAiImage}
                disabled={isGeneratingAiImage}
                className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{isGeneratingAiImage ? 'Generating Photographic Scene...' : 'Synthesize with AI'}</span>
              </button>

              {generatedAiImageUrl && (
                <button
                  type="button"
                  onClick={handleApplyGeneratedImage}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Apply to Scenario</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
